/**
 * Organization Dashboard
 * Frontend logic for the org management dashboard
 */

class OrgDashboard {
  // List pricing shown before a subscription exists. MUST match /corporate and
  // the Stripe prices behind STRIPE_CORPORATE_PRICE_ID /
  // STRIPE_CORPORATE_ANNUAL_PRICE_ID — three places previously disagreed
  // ($100 on the page, $100 charged, $99 printed here).
  static CORPORATE_PRICING = {
    monthly: { amount: 349, interval: 'month' },
    annual:  { amount: 3490, interval: 'year' }
  };

  static currentOrg = null;
  static dashboardData = null;
  static members = [];
  // Viewer-link management is now per-event (no global list / no sidebar tab).
  // The modal is opened via showEventViewerLinks(formId, eventTitle) from the
  // event card. There's exactly one active link per event — the modal auto-
  // creates it the first time, and "Revoke" just deletes it (a fresh click
  // will mint a new one).
  static _currentViewerEventId = null;
  static _currentViewerLink = null;
  static userRole = null;

  // Visibility state
  static _isPageVisible = true;
  static _activeTab = 'events';
  static _initialized = false;

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  static async init() {
    // Wait for Firebase auth
    await this._waitForAuth();

    // Setup global auth listener (once) for sign-in / sign-out transitions
    if (!this._initialized) {
      this._setupAuthListener();
      this._setupVisibilityListener();
    }

    const user = window.firebaseAuth?.currentUser;
    if (!user) {
      // Stop any existing polling & hide dashboard
      // No polling to stop
      this._resetDashboardUI();
      document.getElementById('orgLoading').style.display = 'none';
      if (window.authManager?.showAuthModal) {
        window.authManager.showAuthModal('your organization dashboard');
      }
      return;
    }

    // Load organizations
    await window.OrgContext.loadUserOrgs();
    const orgs = window.OrgContext.orgs;

    document.getElementById('orgLoading').style.display = 'none';

    if (!orgs || orgs.length === 0) {
      // No polling to stop
      this._resetDashboardUI();
      document.getElementById('noOrgState').style.display = 'block';
      // Arrived from a "Start here" button (task #953): straight into setup.
      // Anyone else who lands here without an org keeps the plain choice.
      if (new URLSearchParams(window.location.search).get('start') === '1') this.showCreateOrgModal();
      return;
    }

    // If no org selected, use first one
    let orgId = window.OrgContext.getCurrentOrgId();
    if (!orgId || !orgs.find(o => o.id === orgId)) {
      window.OrgContext.setCurrentOrg(orgs[0].id);
      orgId = orgs[0].id;
    }

    this.currentOrg = orgs.find(o => o.id === orgId);
    this.userRole = this.currentOrg?.myRole;

    // Render header
    this._renderHeader();
    this._applySubscriptionGate();
    this._bindCountsLock();

    // Setup tabs
    this._setupTabs();

    // Task #862: a link straight to an event (?event=) opens it NOW, in
    // parallel with the overview's data. It used to wait for the whole
    // overview, SHOW it, and only then swap it for the event's own "Loading…"
    // — content, then a spinner again. The overview still loads behind it, so
    // "back to events" is instant.
    const deepLinked = !!new URLSearchParams(window.location.search).get('event');
    if (deepLinked && window.OrgEventDetail?.hydrateFromUrl) window.OrgEventDetail.hydrateFromUrl();
    const eventOpen = () => document.getElementById('orgEventDetail')?.style.display === 'block';

    // Load dashboard data
    await this.loadDashboard();

    // Show content — unless an event is open over it.
    if (!eventOpen()) {
      document.getElementById('summaryCards').style.display = 'flex';
      document.getElementById('orgContent').style.display = 'block';
    }

    // Update Create Event button to include org context
    const createEventBtn = document.getElementById('createEventBtn');
    if (createEventBtn) {
      createEventBtn.href = `/org-builder?org=${this.currentOrg.id}`;
    }

    // Show events page link
    this._showEventsPageLink();
    this._renderProfileChecklist();

    // Restrict UI based on role
    this._applyRoleRestrictions();

    // Live polling so stats update incrementally (paused when tab hidden).
    // Not while an event is open: the event view runs its own, and resumes
    // this one when it closes.
    if (!eventOpen()) this._startPolling();
    this._initialized = true;

    // Sidebar collapse toggle — persists across sessions
    this._initSidebarCollapse();
  }

  static _initSidebarCollapse() {
    const toggle = document.getElementById('orgSidebarToggle');
    if (!toggle || toggle.dataset.bound) return;
    toggle.dataset.bound = '1';

    // Hydrate from localStorage
    if (localStorage.getItem('orgSidebarCollapsed') === '1') {
      document.body.classList.add('org-sidebar-collapsed');
    }
    this._updateSidebarToggleTitle(toggle);

    toggle.addEventListener('click', () => {
      const collapsed = document.body.classList.toggle('org-sidebar-collapsed');
      localStorage.setItem('orgSidebarCollapsed', collapsed ? '1' : '0');
      this._updateSidebarToggleTitle(toggle);
    });
  }

  static _updateSidebarToggleTitle(toggle) {
    const collapsed = document.body.classList.contains('org-sidebar-collapsed');
    toggle.title = collapsed ? 'Expand sidebar' : 'Collapse sidebar';
  }

  static _POLL_MS = 30000;
  static _pollTimer = null;

  static _startPolling() {
    if (this._pollTimer) return;
    const tick = () => {
      if (document.visibilityState !== 'hidden' && this.currentOrg) {
        this.loadDashboard().catch(err => console.warn('Dashboard poll failed:', err.message));
      }
    };
    this._pollTimer = setInterval(tick, this._POLL_MS);
    // Kick an immediate refresh the moment the tab becomes visible again
    if (!this._visibilityBound) {
      this._visibilityBound = true;
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && this.currentOrg) {
          this.loadDashboard().catch(() => {});
        }
      });
      window.addEventListener('beforeunload', () => this._stopPolling());
    }
  }

  static _stopPolling() {
    if (this._pollTimer) {
      clearInterval(this._pollTimer);
      this._pollTimer = null;
    }
  }

  static _waitForAuth() {
    return new Promise((resolve) => {
      if (window.firebaseAuth?.currentUser) {
        resolve();
        return;
      }
      const checkAuth = () => {
        if (window.firebaseAuth) {
          window.firebaseAuth.onAuthStateChanged((user) => {
            resolve();
          });
        } else {
          setTimeout(checkAuth, 100);
        }
      };
      checkAuth();
    });
  }

  // ============================================================================
  // LIVE UPDATES — AUTH, VISIBILITY, POLLING
  // ============================================================================

  /**
   * Listen for sign-in / sign-out transitions and re-init the dashboard.
   * Registered once so we never stack duplicate listeners.
   */
  static _setupAuthListener() {
    if (this._authListenerSet) return;
    this._authListenerSet = true;

    window.authManager?.onAuthStateChanged(async (user) => {
      if (user) {
        // User just signed in — bootstrap the dashboard in-place
        document.getElementById('orgLoading').style.display = 'block';
        await this.init();
      } else {
        // User signed out — tear down
        // No polling to stop
        this._resetDashboardUI();
      }
    });
  }

  /**
   * Refresh data when user returns to the tab (no polling).
   */
  static _setupVisibilityListener() {
    if (this._visibilityListenerSet) return;
    this._visibilityListenerSet = true;

    document.addEventListener('visibilitychange', () => {
      this._isPageVisible = !document.hidden;
      if (this._isPageVisible && this._initialized) {
        // Refresh data when user returns to the page
        this._refreshActiveTab();
      }
    });
  }

  /**
   * Hide all dashboard sections so we can re-render cleanly.
   */
  static _resetDashboardUI() {
    document.getElementById('summaryCards').style.display = 'none';
    document.getElementById('orgContent').style.display = 'none';
    document.getElementById('noOrgState').style.display = 'none';
    document.getElementById('orgHeader').innerHTML = '';
    this.dashboardData = null;
    this.members = [];
  }

  // No polling — data refreshes on page visibility change and after user actions

  /** Refresh whichever tab is active right now (used on visibility resume). */
  static _refreshActiveTab() {
    this.loadDashboard();
    if (this._activeTab === 'members') this.loadMembers();
  }

  // ============================================================================
  // HEADER & TABS
  // ============================================================================

  // Mirrors OrganizationService._senderLocalPart on the server.
  // Used to display the per-org sender address in Settings.
  static _senderLocalPart(org) {
    const clean = (s) => String(s || '')
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 32);
    const fromName = clean(org?.name);
    if (fromName && fromName.length >= 2) return fromName;
    const fromSlug = clean(org?.slug);
    if (fromSlug && fromSlug.length >= 2) return fromSlug;
    return 'events';
  }

  static _applyBrand() {
    const color = this.currentOrg?.settings?.branding?.primaryColor;
    const root = document.documentElement;

    if (!color || !/^#[0-9a-fA-F]{6}$/.test(color)) {
      ['--brand', '--brand-deep', '--brand-soft', '--brand-on', '--brand-hover', '--brand-hover-deep',
       '--sidebar-bg', '--sidebar-bg-2', '--sidebar-active-bg']
        .forEach(p => root.style.removeProperty(p));
      return;
    }

    root.style.setProperty('--brand', color);
    root.style.setProperty('--brand-deep', this._darken(color, 0.18));
    root.style.setProperty('--brand-soft', this._mix(color, '#ffffff', 0.92));
    // Hover: the same gradient a step darker, so the button stays the brand.
    root.style.setProperty('--brand-hover', this._darken(color, 0.06));
    root.style.setProperty('--brand-hover-deep', this._darken(color, 0.24));

    // Auto text color for contrast on the brand background
    root.style.setProperty('--brand-on', this._textOn(color));

    // Sidebar: brand as a subtle undertone — heavy mix with deep neutral
    root.style.setProperty('--sidebar-bg',   this._mix(color, '#0a0618', 0.92));
    root.style.setProperty('--sidebar-bg-2', this._mix(color, '#0a0618', 0.86));

    // Active nav item: a slightly more saturated brand-tinted dark
    root.style.setProperty('--sidebar-active-bg', this._mix(color, '#0a0618', 0.55));
  }

  /**
   * Pick black or white text for best contrast on a given hex color.
   * Uses relative luminance per WCAG.
   */
  static _textOn(hex) {
    const n = parseInt(hex.slice(1), 16);
    const r = ((n >> 16) & 255) / 255;
    const g = ((n >> 8) & 255) / 255;
    const b = (n & 255) / 255;
    const lin = (c) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
    return L > 0.55 ? '#0a0618' : '#ffffff';
  }

  static _darken(hex, amount) {
    const n = parseInt(hex.slice(1), 16);
    const r = Math.max(0, ((n >> 16) & 255) - Math.round(255 * amount));
    const g = Math.max(0, ((n >> 8) & 255) - Math.round(255 * amount));
    const b = Math.max(0, (n & 255) - Math.round(255 * amount));
    return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
  }

  static _mix(hexA, hexB, weight) {
    const a = parseInt(hexA.slice(1), 16);
    const b = parseInt(hexB.slice(1), 16);
    const w = Math.max(0, Math.min(1, weight));
    const mix = (sa, sb) => Math.round(sa * (1 - w) + sb * w);
    const r = mix((a >> 16) & 255, (b >> 16) & 255);
    const g = mix((a >> 8) & 255, (b >> 8) & 255);
    const bl = mix(a & 255, b & 255);
    return '#' + [r, g, bl].map(v => v.toString(16).padStart(2, '0')).join('');
  }

  static _renderHeader() {
    const org = this.currentOrg;
    const orgs = window.OrgContext.orgs;
    this._applyBrand();

    const initial = this._escapeHtml(org.name.charAt(0).toUpperCase());
    const logoHtml = org.logo
      ? `<img src="${this._escapeHtml(org.logo)}" alt="${initial}" class="org-logo" onerror="var d=document.createElement('div');d.className='org-logo-placeholder';d.textContent='${initial}';this.parentNode.replaceChild(d,this);">`
      : `<div class="org-logo-placeholder">${initial}</div>`;

    // Org switcher if multiple orgs
    let switcherHtml = '';
    if (orgs.length > 1) {
      const options = orgs.map(o =>
        `<option value="${o.id}" ${o.id === org.id ? 'selected' : ''}>${this._escapeHtml(o.name)}</option>`
      ).join('');
      switcherHtml = `
        <select class="form-select form-select-sm d-inline-block ms-3" style="width: auto; background: rgba(255,255,255,0.1); color: white; border-color: rgba(255,255,255,0.2);" onchange="OrgDashboard.switchOrg(this.value)">
          ${options}
        </select>`;
    }

    const canManage = this.userRole === 'admin' || this.userRole === 'owner';
    const logoUploadHtml = canManage ? `
            <div class="org-logo-upload-wrapper" title="Change logo" onclick="OrgDashboard.triggerLogoUpload()">
              ${logoHtml}
              <div class="org-logo-upload-overlay"><i class="bi bi-camera"></i></div>
              <input type="file" id="orgLogoFileInput" accept="image/jpeg,image/png" style="display:none;">
            </div>` : logoHtml;

    let switcherBtnHtml = '';
    let switcherMenuHtml = '';
    if (orgs.length > 1) {
      switcherBtnHtml = '<button class="org-switcher-btn" title="Switch organization" onclick="OrgDashboard._toggleOrgSwitcher()"><i class="bi bi-chevron-expand"></i></button>';
      const items = orgs.map(o => {
        const isActive = o.id === org.id;
        const check = isActive ? '<i class="bi bi-check2"></i>' : '';
        return `<button class="org-switcher-item ${isActive ? 'active' : ''}" onclick="OrgDashboard.switchOrg('${o.id}')"><span>${this._escapeHtml(o.name)}</span>${check}</button>`;
      }).join('');
      switcherMenuHtml = `<div class="org-switcher-menu" id="orgSwitcherMenu" style="display:none;">${items}</div>`;
    }

    document.getElementById('orgHeader').innerHTML = `
      <div class="org-identity">
        ${logoUploadHtml}
        <div class="org-identity-info">
          <div class="org-name" title="${this._escapeHtml(org.name)}">${this._escapeHtml(org.name)}</div>
          <div class="org-identity-meta">
            <i class="bi bi-shield-check"></i>${this._capitalize(this.userRole || 'viewer')}
          </div>
        </div>
        ${switcherBtnHtml}
      </div>
      ${switcherMenuHtml}`;

    // Set up logo file input handler
    const logoInput = document.getElementById('orgLogoFileInput');
    if (logoInput) {
      logoInput.addEventListener('change', (e) => {
        const file = e.target.files[0];
        if (file) this._handleLogoUpload(file);
      });
    }
  }

  static _toggleOrgSwitcher() {
    const menu = document.getElementById('orgSwitcherMenu');
    if (menu) menu.style.display = menu.style.display === 'none' ? 'block' : 'none';
  }

  static _setupTabs() {
    document.querySelectorAll('#orgTabs .nav-link, #orgTabs .org-nav-item').forEach(btn => {
      btn.addEventListener('click', () => {
        // If an event detail is open, close it so the sidebar tab is visible
        const detail = document.getElementById('orgEventDetail');
        if (detail && detail.style.display !== 'none' && window.OrgEventDetail?.close) {
          window.OrgEventDetail.close();
        }

        // Remove active from all tabs and panes
        document.querySelectorAll('#orgTabs .nav-link, #orgTabs .org-nav-item').forEach(b => b.classList.remove('active'));
        document.querySelectorAll('.tab-pane').forEach(p => p.classList.remove('active'));

        // Activate clicked tab
        btn.classList.add('active');
        const tab = btn.dataset.tab;
        document.getElementById(`tab-${tab}`).classList.add('active');

        // Leaving Settings: save what is pending now rather than after the pause.
        if (this._activeTab === 'settings' && tab !== 'settings') this._flushSettings();
        this._activeTab = tab;

        // Lazy load tab data
        if (tab === 'members') this.loadMembers();
        if (tab === 'activity') this._loadActivity();
        if (tab === 'settings') this._populateSettings();
        if (tab === 'contacts') window.OrgContacts?.init();
      });
    });
  }

  static _applyRoleRestrictions() {
    const role = this.userRole;
    if (role === 'viewer') {
      // Hide management buttons for viewers
      document.querySelectorAll('[data-admin-only]').forEach(el => el.style.display = 'none');
    }
    if (role !== 'admin') {
      document.getElementById('addMemberBtn')?.setAttribute('style', 'display:none!important');
      // The tab button itself. Since the 2026-04-19 redesign the buttons sit
      // directly in <nav id="orgTabs">, so removing the parentElement (the old
      // <li> wrapper) deleted the WHOLE nav for every editor and viewer.
      // Members goes too: GET /members is verifyOrgRole('admin').
      document.querySelector('#orgTabs [data-tab="settings"]')?.remove();
      document.querySelector('#orgTabs [data-tab="members"]')?.remove();
    }
  }

  // ============================================================================
  // DASHBOARD DATA
  // ============================================================================

  static async loadDashboard() {
    try {
      const data = await window.OrgContext.orgFetch('/dashboard');
      const prev = this.dashboardData;
      this.dashboardData = data;

      // ---- Incremental summary stat updates (animate on change) ----
      const s = data.summary || {};
      this._updateStat('statEvents', s.totalEvents || 0);
      this._updateStat('statResponses', s.totalResponses || 0);
      this._updateStat('statAttending', s.totalAttending || 0);
      this._updateStat('statGuests', s.totalGuests || 0);
      this._lockSummaryCounts((s.totalResponses || 0) > 0);

      // ---- Only re-render event cards if the data actually changed ----
      const formsFingerprint = this.needsSubscription() + JSON.stringify((data.forms || []).map(f => ({
        id: f.id, title: f.title, status: f.status,
        rc: f.responseCount, s: f.stats, date: f.eventDate, ended: f.ended
      })));
      const prevFingerprint = prev ? this._prevLockState + JSON.stringify((prev.forms || []).map(f => ({
        id: f.id, title: f.title, status: f.status,
        rc: f.responseCount, s: f.stats, date: f.eventDate, ended: f.ended
      }))) : null;

      this._prevLockState = this.needsSubscription();
      if (formsFingerprint !== prevFingerprint) {
        this._renderEvents(data.forms || []);
      }

      // Activity is now its own tab — loaded lazily when the user opens it,
      // not on every dashboard refresh.

      // ---- Subscription / lifecycle banner (only fetches once per load cycle,
      //      but the polling loop will keep it fresh) ----
      if (!this._lifecycleLoaded) {
        this._lifecycleLoaded = true;
        this._loadSubscription();
      }

    } catch (err) {
      console.error('Failed to load dashboard:', err);
      // Only show error UI if we don't already have data (don't nuke the screen on transient poll failure)
      if (!this.dashboardData) {
        document.getElementById('eventsList').innerHTML = `
          <div class="empty-state">
            <div class="empty-state-icon"><i class="bi bi-exclamation-triangle"></i></div>
            <h4>Error Loading Dashboard</h4>
            <p>${this._escapeHtml(err.message)}</p>
            <button class="btn btn-org-primary" onclick="OrgDashboard.loadDashboard()">Retry</button>
          </div>`;
      }
    }
  }

  /**
   * Update a stat counter element with optional flash animation on change.
   */
  static _updateStat(elementId, newValue) {
    const el = document.getElementById(elementId);
    if (!el) return;
    const current = el.textContent.trim();
    const next = String(newValue);
    if (current !== next) {
      el.textContent = next;
      el.classList.remove('stat-flash');
      // Force reflow so the animation re-triggers
      void el.offsetWidth;
      el.classList.add('stat-flash');
    }
  }

  static _renderEvents(forms) {
    const container = document.getElementById('eventsList');

    // Apply filters
    const statusFilter = document.getElementById('eventStatusFilter').value;
    const sortFilter = document.getElementById('eventSortFilter').value;

    let filtered = [...forms];
    if (statusFilter !== 'all') {
      filtered = filtered.filter(f => f.status === statusFilter);
    }

    if (sortFilter === 'newest') {
      filtered.sort((a, b) => (b.createdAt?._seconds || 0) - (a.createdAt?._seconds || 0));
    } else if (sortFilter === 'oldest') {
      filtered.sort((a, b) => (a.createdAt?._seconds || 0) - (b.createdAt?._seconds || 0));
    } else if (sortFilter === 'responses') {
      filtered.sort((a, b) => b.responseCount - a.responseCount);
    }

    // Events exist but the status filter hides them all: say so, rather than
    // "No Events Yet" over an org that has events.
    if (filtered.length === 0 && forms.length > 0) {
      container.innerHTML = `
        <div class="empty-state">
          <div class="empty-state-icon"><i class="bi bi-funnel"></i></div>
          <h4>No events match this filter</h4>
          <p>Choose another status to see your events.</p>
        </div>`;
      return;
    }

    if (filtered.length === 0) {
      // Both ways in, side by side (task #952). A viewer can do neither, so
      // a viewer gets neither button.
      const canEdit = ['admin', 'owner', 'editor'].includes(this.userRole);
      container.innerHTML = `
        <div class="empty-state">
          <div class="empty-state-icon"><i class="bi bi-calendar-plus"></i></div>
          <h4>No Events Yet</h4>
          <p>${canEdit ? 'Create your first event or assign an existing form to get started.' : 'Your team has not added any events yet.'}</p>
          ${canEdit ? `<div class="d-flex flex-wrap justify-content-center gap-2">
            <a href="/org-builder?org=${this.currentOrg.id}" target="_blank" class="btn btn-org-primary"><i class="bi bi-plus-circle me-1"></i>Create Event</a>
            <button type="button" class="btn btn-org-outline" onclick="OrgDashboard.showAssignFormModal()"><i class="bi bi-link me-1"></i>Assign form</button>
          </div>` : ''}
        </div>`;
      return;
    }

    // Events that are over go under a "Past events" heading below the rest,
    // shut until the host opens it (task #942) — an org that has run a year of
    // events otherwise scrolls past all of them to reach next week's. "Over" is
    // the server's `ended`, the same rule that closes RSVPs. Open/shut is kept
    // across the 30s refresh, which redraws this list.
    const upcoming = filtered.filter(f => !f.ended);
    const past = filtered.filter(f => f.ended);
    const pastOpen = !!this._pastEventsOpen;
    container.innerHTML = (upcoming.length
        ? `<div class="row g-3">${upcoming.map(f => this._eventCardHtml(f)).join('')}</div>`
        : `<p class="org-no-upcoming">No upcoming events.</p>`)
      + (past.length ? `
        <div class="org-past-events">
          <button type="button" class="org-past-toggle" aria-expanded="${pastOpen}" aria-controls="orgPastEventsList">
            <i class="bi bi-chevron-right" aria-hidden="true"></i>
            <span>Past events</span>
            <span class="org-past-count">${past.length}</span>
          </button>
          <div class="row g-3" id="orgPastEventsList"${pastOpen ? '' : ' hidden'}>${past.map(f => this._eventCardHtml(f)).join('')}</div>
        </div>` : '');
    const pastToggle = container.querySelector('.org-past-toggle');
    if (pastToggle) pastToggle.onclick = () => {
      this._pastEventsOpen = !this._pastEventsOpen;
      pastToggle.setAttribute('aria-expanded', String(this._pastEventsOpen));
      document.getElementById('orgPastEventsList').hidden = !this._pastEventsOpen;
    };

    // Setup filter listeners
    document.getElementById('eventStatusFilter').onchange = () => this._renderEvents(forms);
    document.getElementById('eventSortFilter').onchange = () => this._renderEvents(forms);
  }

  static _eventCardHtml(form) {
    const s = form.stats || {};
    const dateStr = form.eventDate || 'No date set';
    const statusClass = form.status === 'active' ? 'text-success' : 'text-muted';
    const statusIcon = form.status === 'active' ? 'bi-circle-fill' : 'bi-circle';

    const canEdit = this.userRole === 'admin' || this.userRole === 'editor';
    const countsLocked = this._countsLocked(form.responseCount > 0);

    return `
      <div class="event-card">
        <div class="event-card-main" role="button" tabindex="0" onclick="OrgEventDetail.open('${form.id}','responses')" onkeydown="if(event.target===this&&(event.key==='Enter'||event.key===' ')){event.preventDefault();OrgEventDetail.open('${form.id}','responses');}">
          <div class="event-card-header">
            <div>
              <h6 class="event-card-title">${this._escapeHtml(form.title)}</h6>
              <div class="event-card-date">
                <i class="bi bi-calendar3"></i> ${this._escapeHtml(dateStr)}
                ${s.pending > 0 ? `<span class="approval-badge" title="Waiting for your approval - open the event to approve or deny"><i class="bi bi-clock"></i>${s.pending} pending</span>` : ''}
              </div>
            </div>
            <span class="event-card-status ${statusClass}"><i class="bi ${statusIcon}"></i></span>
          </div>
          <div class="event-card-stats${countsLocked ? ` is-count-locked" role="button" tabindex="0" title="${OrgDashboard.COUNTS_LOCK_TEXT}` : ''}">
            <div class="event-stat">
              <div class="event-stat-value">${form.responseCount}</div>
              <div class="event-stat-label">Total</div>
            </div>
            <div class="event-stat">
              <div class="event-stat-value" style="color: var(--org-green);">${s.attending || 0}</div>
              <div class="event-stat-label">Yes</div>
            </div>
            <div class="event-stat">
              <div class="event-stat-value" style="color: var(--org-rose);">${s.notAttending || 0}</div>
              <div class="event-stat-label">No</div>
            </div>
            <div class="event-stat">
              <div class="event-stat-value" style="color: var(--org-gold-deep);">${s.totalGuests || 0}</div>
              <div class="event-stat-label">Guests</div>
            </div>
          </div>
        </div>
        <div class="event-card-actions">
          <button type="button" class="btn btn-sm-icon" title="Copy form link" onclick="OrgDashboard.copyFormLink('${form.id}')">
            <i class="bi bi-link-45deg"></i>
          </button>
          <button type="button" class="btn btn-sm-icon" title="Read-only viewer link for stakeholders" onclick="OrgDashboard.showEventViewerLinks('${form.id}', ${this._jsAttr(form.title || '')})">
            <i class="bi bi-eye"></i>
          </button>
          ${canEdit ? `
            <button class="btn btn-sm-icon" title="Send for this event — invite to RSVP or send event updates" onclick="OrgCampaign.open({preselectedEventId: '${form.id}', chooser: true, eventName: ${this._jsAttr(form.title || '')}})">
              <i class="bi bi-send"></i>
            </button>
            <button class="btn btn-sm-icon ${form.emailNotifications?.enabled ? 'is-active' : ''}" title="${form.emailNotifications?.enabled ? 'Response notifications on' : 'Response notifications'}" onclick="OrgDashboard.openNotificationsModal('${form.id}')">
              <i class="bi ${form.emailNotifications?.enabled ? 'bi-bell-fill' : 'bi-bell'}"></i>
            </button>
            <a href="${this._getEditLink(form)}" target="_blank" class="btn btn-sm-icon" title="Edit form">
              <i class="bi bi-pencil"></i>
            </a>
            <button class="btn btn-sm-icon ${form.isTemplate ? 'is-active' : ''}" title="${form.isTemplate ? 'Remove from templates' : 'Save as template'}" onclick="OrgDashboard.toggleFormTemplate('${form.id}', ${!!form.isTemplate}, ${this._jsAttr(form.title || '')})">
              <i class="bi ${form.isTemplate ? 'bi-bookmark-fill' : 'bi-bookmark-plus'}"></i>
            </button>
            <button class="btn btn-sm-icon" title="Clone event" onclick="OrgDashboard.cloneForm('${form.id}')">
              <i class="bi bi-files"></i>
            </button>
            ${this.userRole === 'admin' ? `<button class="btn btn-sm-icon" title="Remove from org" onclick="OrgDashboard.removeForm('${form.id}')">
              <i class="bi bi-x-lg"></i>
            </button>` : ''}
          ` : ''}
        </div>
      </div>`;
  }

  static _getEditLink(form) {
    const lang = form.defaultLanguage || 'en';
    const langPaths = {
      'en': '/org-builder', 'es': '/es/builder', 'fr': '/fr/builder',
      'pt': '/pt/builder', 'de': '/de/builder', 'he': '/he/builder',
      'ar': '/ar/builder'
    };
    const path = langPaths[lang] || langPaths['en'];
    return `${path}.html?formId=${form.id}`;
  }

  // ============================================================================
  // TEMPLATES
  // ============================================================================

  static async showTemplateLibrary() {
    const modalEl = document.getElementById('templateLibraryModal');
    if (!modalEl) return;
    const list = document.getElementById('templateLibraryList');
    list.innerHTML = '<div class="text-center py-3"><div class="spinner-border spinner-border-sm text-primary"></div></div>';

    const modal = new bootstrap.Modal(modalEl);
    modal.show();

    try {
      const data = await window.OrgContext.orgFetch('/templates');
      const templates = data.templates || [];
      this._renderTemplateLibrary(templates);
    } catch (err) {
      list.innerHTML = `<p class="text-muted small">Could not load templates: ${this._escapeHtml(err.message)}</p>`;
    }
  }

  static _renderTemplateLibrary(templates) {
    const list = document.getElementById('templateLibraryList');
    if (!list) return;

    if (templates.length === 0) {
      list.innerHTML = `
        <div class="empty-state" style="padding: 2rem 1rem;">
          <div class="empty-state-icon"><i class="bi bi-files"></i></div>
          <h4>No templates yet</h4>
          <p>Save any event as a template using the <i class="bi bi-bookmark-plus"></i> icon on its card.</p>
        </div>`;
      return;
    }

    // The planner's own name for the template leads; the event it was saved
    // from is background, and the message count says what "use" will produce
    // (task #619).
    list.innerHTML = templates.map(t => {
      const msgs = Number(t.messageCount) || 0;
      const carries = msgs
        ? `Form + ${msgs} message${msgs === 1 ? '' : 's'} with their reminders`
        : 'Form and questions only — no messages saved on it yet';
      return `
      <div class="template-row">
        <div class="template-row-info">
          <div class="template-row-title">${this._escapeHtml(t.title)}</div>
          ${t.description ? `<div class="template-row-desc">${this._escapeHtml(t.description)}</div>` : ''}
          <div class="template-row-desc"><i class="bi bi-box-seam me-1"></i>${carries}</div>
          <div class="template-row-meta">${t.eventType ? this._escapeHtml(t.eventType) + ' · ' : ''}${t.savedFrom && t.savedFrom !== t.title ? 'From ' + this._escapeHtml(t.savedFrom) + ' · ' : ''}Saved ${t.savedAt ? this._relativeTime(t.savedAt) : 'recently'}</div>
        </div>
        <button class="btn btn-org-primary btn-sm" onclick="OrgDashboard.useTemplate('${t.id}')">
          <i class="bi bi-plus-circle"></i><span>Use template</span>
        </button>
      </div>`;
    }).join('');
  }

  static async useTemplate(templateId) {
    try {
      const res = await window.OrgContext.orgFetch(`/forms/${templateId}/clone`, { method: 'POST' });
      const msgs = Number(res?.form?.messagesCloned) || 0;
      this._showToast(msgs
        ? `Event created from template, with ${msgs} message${msgs === 1 ? '' : 's'} saved as drafts — add the guest list and send`
        : 'Event created from template');
      bootstrap.Modal.getInstance(document.getElementById('templateLibraryModal'))?.hide();
      this.loadDashboard();
    } catch (err) {
      this._showToast('Could not use template: ' + err.message, 'danger');
    }
  }

  static async toggleFormTemplate(formId, currentlyTemplate, eventTitle = '') {
    // Removing needs no conversation. Saving does: a planner keeps SEVERAL
    // templates ("Wedding Standard", "Corporate Gala"), and the client's event
    // title is a poor name for the one you reach for on the next client.
    if (currentlyTemplate) {
      return this._writeTemplateStatus(formId, false, '', '');
    }
    this._promptForTemplateName(eventTitle, (name, description) =>
      this._writeTemplateStatus(formId, true, name, description));
  }

  static _promptForTemplateName(defaultName, onSave) {
    const esc = (s) => this._escapeHtml(String(s == null ? '' : s));
    const el = document.createElement('div');
    el.className = 'modal fade';
    el.innerHTML = `
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title"><i class="bi bi-bookmark-plus me-2"></i>Save as template</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <p class="text-muted small">This saves the form, its questions and its message schedule. The guest list is never part of a template.</p>
            <label class="form-label small fw-semibold" for="tplName">Template name</label>
            <input type="text" class="form-control mb-1" id="tplName" maxlength="80"
                   placeholder="Wedding Standard" value="${esc(defaultName)}">
            <div class="form-text mb-3">What you'll look for next time — not this client's event name.</div>
            <label class="form-label small fw-semibold" for="tplDesc">Note <span class="text-muted fw-normal">(optional)</span></label>
            <input type="text" class="form-control" id="tplDesc" maxlength="300"
                   placeholder="Full RSVP set, three reminders, meal choice">
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-org-primary" id="tplSave">Save template</button>
          </div>
        </div>
      </div>`;
    document.body.appendChild(el);
    const modal = new bootstrap.Modal(el);
    el.addEventListener('hidden.bs.modal', () => el.remove());
    const save = () => {
      const name = el.querySelector('#tplName').value.trim();
      if (!name) { el.querySelector('#tplName').focus(); return; }
      modal.hide();
      onSave(name, el.querySelector('#tplDesc').value.trim());
    };
    el.querySelector('#tplSave').addEventListener('click', save);
    el.querySelector('#tplName').addEventListener('keydown', (e) => { if (e.key === 'Enter') save(); });
    el.addEventListener('shown.bs.modal', () => el.querySelector('#tplName').select());
    modal.show();
  }

  static async _writeTemplateStatus(formId, isTemplate, name, description) {
    try {
      await window.OrgContext.orgFetch(`/forms/${formId}/template`, {
        method: 'PUT',
        body: JSON.stringify({ isTemplate, name, description })
      });
      this._showToast(isTemplate ? `Saved as template "${name}"` : 'Removed from templates');
      this.loadDashboard();
    } catch (err) {
      this._showToast('Could not update template: ' + err.message, 'danger');
    }
  }

  static async _loadActivity() {
    const list = document.getElementById('activityList');
    if (!list) return;

    try {
      // Higher limit now that this is a dedicated tab — admins want history,
      // not just a peek
      const data = await window.OrgContext.orgFetch('/activity?limit=50');
      const items = data.activities || [];
      if (items.length === 0) {
        list.innerHTML = `
          <div class="empty-state">
            <div class="empty-state-icon"><i class="bi bi-activity"></i></div>
            <h4>No activity yet</h4>
            <p>Approvals, RSVPs, and team changes will show up here as they happen.</p>
          </div>`;
        return;
      }
      // Unsubscribed workspace (task #1067): the server sent WHEN guests
      // replied but not who, so the feed is drawn blurred behind a Subscribe
      // card - real activity, nothing readable to take the blur off.
      if (items.some(a => a.type === 'response_locked')) {
        list.innerHTML = `
          <div class="activity-paywall">
            <div class="activity-paywall-feed" aria-hidden="true">${items.map(a => this._renderActivityItem(a)).join('')}</div>
            <div class="activity-paywall-card">
              <i class="bi bi-lock"></i>
              <p>Subscribe to view real-time guest activity and responses.</p>
              <button class="btn btn-org-primary btn-sm" type="button" onclick="OrgDashboard.openSubscribeModal()">
                <i class="bi bi-rocket-takeoff"></i> Subscribe
              </button>
            </div>
          </div>`;
        return;
      }
      list.innerHTML = items.map(a => this._renderActivityItem(a)).join('');
    } catch (err) {
      console.warn('Activity load failed:', err);
      list.innerHTML = `<p class="text-danger small mb-0">Could not load activity: ${this._escapeHtml(err.message)}</p>`;
    }
  }

  static _renderActivityItem(a) {
    const ICONS = {
      response_attending: { i: 'bi-check2-circle', cls: 'act-green', verb: 'RSVP\u2019d yes to' },
      response_declined: { i: 'bi-x-circle', cls: 'act-rose', verb: 'declined' },
      response_pending: { i: 'bi-hourglass-split', cls: 'act-gold', verb: 'submitted a response to' },
      response_denied: { i: 'bi-slash-circle', cls: 'act-rose', verb: 'was denied for' },
      response_approved: { i: 'bi-check-all', cls: 'act-green', verb: 'was approved for' },
      member_added: { i: 'bi-person-plus', cls: 'act-purple', verb: 'joined as' },
      // Shown blurred only: the server leaves out who and which event (task #1067).
      response_locked: { i: 'bi-check2-circle', cls: 'act-green', verb: 'RSVP\u2019d to' }
    };
    if (a.type === 'response_locked') a = Object.assign({}, a, { actor: 'Guest name', subject: 'Your event' });
    const meta = ICONS[a.type] || { i: 'bi-circle', cls: 'act-purple', verb: '\u2014' };
    const time = this._relativeTime(a.at);
    return `
      <div class="activity-item">
        <div class="activity-icon ${meta.cls}"><i class="bi ${meta.i}"></i></div>
        <div class="activity-body">
          <div class="activity-text"><strong>${this._escapeHtml(a.actor)}</strong> ${meta.verb} <strong>${this._escapeHtml(a.subject)}</strong></div>
          <div class="activity-time">${time}</div>
        </div>
      </div>`;
  }

  static _relativeTime(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  // ============================================================================
  // MEMBERS
  // ============================================================================

  static async loadMembers() {
    try {
      const data = await window.OrgContext.orgFetch('/members');
      const newMembers = data.members || [];
      // Only re-render if the member list actually changed
      const fingerprint = JSON.stringify(newMembers.map(m => ({ u: m.userId, r: m.role, e: m.email, n: m.displayName })));
      const prevFingerprint = JSON.stringify(this.members.map(m => ({ u: m.userId, r: m.role, e: m.email, n: m.displayName })));
      if (fingerprint !== prevFingerprint) {
        this.members = newMembers;
        this._renderMembers();
      }
    } catch (err) {
      // Don't nuke existing UI on transient poll failure
      if (this.members.length === 0) {
        document.getElementById('membersList').innerHTML =
          `<p class="text-danger small">Failed to load members: ${this._escapeHtml(err.message)}</p>`;
      }
    }
  }

  static _renderMembers() {
    const container = document.getElementById('membersList');

    if (this.members.length === 0) {
      container.innerHTML = '<p class="text-muted">No members found.</p>';
      return;
    }

    container.innerHTML = this.members.map(m => {
      const initials = (m.displayName || m.email || '?').charAt(0).toUpperCase();
      if (m.pending) {
        // Invited, no account yet (task #861).
        const isAdminP = this.userRole === 'admin';
        return `
        <div class="member-row member-row-pending">
          <div class="d-flex align-items-center">
            <div class="member-avatar">${initials}</div>
            <div class="member-info">
              <div class="member-name">${this._escapeHtml(m.email)}</div>
              <div class="member-email">Invited — joins when they create their account</div>
            </div>
          </div>
          <div class="d-flex align-items-center gap-2">
            <span class="role-badge role-${this._escapeHtml(m.role)}">${this._escapeHtml(m.role)}</span>
            <span class="badge text-bg-light">Invited</span>
            ${isAdminP ? `<button class="btn btn-sm-icon" title="Cancel invite" onclick="OrgDashboard.cancelInvite('${this._escapeHtml(m.email)}')"><i class="bi bi-x-lg"></i></button>` : ''}
          </div>
        </div>`;
      }
      const roleClass = `role-${m.role}`;
      const isAdmin = this.userRole === 'admin';
      const isSelf = m.userId === window.firebaseAuth?.currentUser?.uid;

      return `
        <div class="member-row">
          <div class="d-flex align-items-center">
            <div class="member-avatar">${initials}</div>
            <div class="member-info">
              <div class="member-name">${this._escapeHtml(m.displayName || m.email)}</div>
              <div class="member-email">${this._escapeHtml(m.email)}</div>
            </div>
          </div>
          <div class="d-flex align-items-center gap-2">
            <span class="role-badge ${roleClass}">${m.role}</span>
            ${isAdmin && !isSelf ? `
              <select class="form-select form-select-sm" style="width: auto; font-size: 0.75rem;" onchange="OrgDashboard.updateMemberRole('${m.userId}', this.value)">
                <option value="viewer" ${m.role === 'viewer' ? 'selected' : ''}>Viewer</option>
                <option value="editor" ${m.role === 'editor' ? 'selected' : ''}>Editor</option>
                <option value="admin" ${m.role === 'admin' ? 'selected' : ''}>Admin</option>
              </select>
              <button class="btn btn-sm-icon" title="Remove member" onclick="OrgDashboard.removeMember('${m.userId}')">
                <i class="bi bi-trash"></i>
              </button>
            ` : ''}
          </div>
        </div>`;
    }).join('');
  }

  static showAddMemberModal() {
    document.getElementById('memberEmail').value = '';
    document.getElementById('memberRole').value = 'viewer';
    new bootstrap.Modal(document.getElementById('addMemberModal')).show();
  }

  static async addMember() {
    const email = document.getElementById('memberEmail').value.trim();
    const role = document.getElementById('memberRole').value;

    if (!email) {
      this._showToast('Please enter an email address', 'warning');
      return;
    }

    try {
      const res = await window.OrgContext.orgFetch('/members', {
        method: 'POST',
        body: JSON.stringify({ email, role })
      });
      bootstrap.Modal.getInstance(document.getElementById('addMemberModal'))?.hide();
      // Say what actually happened (task #861): added + emailed, or invited
      // because they have no account yet, and the role they got.
      const m = (res && res.member) || {};
      const roleLabel = { viewer: 'Viewer', editor: 'Editor', admin: 'Admin' }[m.role || role] || role;
      const mailed = m.emailSent === false ? ' (the invitation email could not be sent; let them know yourself)' : ' and emailed an invitation';
      this._showToast(m.pending
        ? `${email} has no QuikRSVP account yet: invited as ${roleLabel}${m.emailSent === false ? ' (email not sent)' : ''}. They join as soon as they create their account with this address.`
        : `${email} added as ${roleLabel}${mailed}`, m.emailSent === false ? 'warning' : undefined);
      this.loadMembers();
    } catch (err) {
      this._showToast('Error: ' + err.message, 'danger');
    }
  }

  static async cancelInvite(email) {
    if (!confirm(`Cancel the invitation for ${email}?`)) return;
    try {
      await window.OrgContext.orgFetch(`/invites/${encodeURIComponent(email)}`, { method: 'DELETE' });
      this._showToast('Invitation cancelled');
      this.loadMembers();
    } catch (err) {
      this._showToast('Error: ' + err.message, 'danger');
    }
  }

  static async updateMemberRole(userId, newRole) {
    try {
      await window.OrgContext.orgFetch(`/members/${userId}`, {
        method: 'PUT',
        body: JSON.stringify({ role: newRole })
      });
      this._showToast('Role updated');
      this.loadMembers();
    } catch (err) {
      this._showToast('Error: ' + err.message, 'danger');
    }
  }

  static async removeMember(userId) {
    if (!confirm('Remove this member from the organization?')) return;

    try {
      await window.OrgContext.orgFetch(`/members/${userId}`, { method: 'DELETE' });
      this._showToast('Member removed');
      this.loadMembers();
    } catch (err) {
      this._showToast('Error: ' + err.message, 'danger');
    }
  }

  // ============================================================================
  // VIEWER LINKS
  // ============================================================================

  /**
   * Open the per-event Viewer Link modal. Auto-fetches or auto-creates the
   * single active link for this event, displays its URL with a Copy button,
   * and exposes a Revoke action and a collapsible column-visibility config.
   */
  static showEventViewerLinks(formId, eventTitle) {
    this._currentViewerEventId = formId;
    this._currentViewerLink = null;
    document.getElementById('viewerLinkEventName').textContent = eventTitle || '';
    document.getElementById('viewerLinkRevokeBtn').style.display = 'none';
    document.getElementById('viewerLinkBody').innerHTML =
      '<div class="text-center py-3"><div class="spinner-border spinner-border-sm text-primary"></div></div>';
    // Collapse the columns config to its default closed state when re-opening
    const details = document.getElementById('viewerColumnsDetails');
    if (details) details.open = false;
    this._renderViewerColumnsConfig();
    new bootstrap.Modal(document.getElementById('viewerLinkModal')).show();
    this._loadOrCreateEventLink();
  }

  /**
   * Render the checkbox list inside the "Choose which columns viewers can see"
   * collapsible. Reads the current viewerHiddenColumns from the cached form
   * data; toggles save immediately via PUT /forms/:formId/viewer-columns.
   */
  static _renderViewerColumnsConfig() {
    const container = document.getElementById('viewerColumnsConfig');
    if (!container) return;
    const form = this.dashboardData?.forms?.find(f => f.id === this._currentViewerEventId);
    if (!form) {
      container.innerHTML = '<p class="text-muted small mb-0">Event details unavailable.</p>';
      return;
    }

    // Same column universe as the admin spreadsheet, minus admin-only ones
    // (checkbox, actions). Custom fields are pulled from the form schema.
    const cols = [
      { key: 'guestName',       label: 'Name' },
      { key: 'status',          label: 'Status' },
      { key: '_total',          label: 'Total guests' },
      { key: 'email',           label: 'Email' },
      { key: 'phone',           label: 'Phone' },
      { key: 'company',         label: 'Company' },
      { key: 'jobTitle',        label: 'Job title' },
      { key: 'department',      label: 'Department' },
      { key: 'dietary',         label: 'Dietary' },
      { key: 'transportation',  label: 'Transport' },
      { key: 'notes',           label: 'Notes' },
      { key: 'heardAboutUs',    label: 'Heard about us' },
      { key: 'submittedAt',     label: 'Submitted' }
    ];
    (form.customFields || []).forEach(f => {
      if (!f || f.type === 'message') return;
      cols.push({ key: `cf:${f.id}`, label: f.label || f.id });
    });

    const hidden = new Set(form.viewerHiddenColumns || []);
    container.innerHTML = cols.map(c => `
      <label class="viewer-columns-item">
        <input type="checkbox" data-col-key="${this._escapeHtml(c.key)}" ${hidden.has(c.key) ? '' : 'checked'}>
        <span>${this._escapeHtml(c.label)}</span>
      </label>`).join('');

    container.querySelectorAll('input[data-col-key]').forEach(cb => {
      cb.addEventListener('change', async (e) => {
        const key = e.target.dataset.colKey;
        if (e.target.checked) hidden.delete(key); else hidden.add(key);
        const list = [...hidden];
        // Optimistically update cached form so the modal stays in sync
        form.viewerHiddenColumns = list;
        try {
          await window.OrgContext.orgFetch(`/forms/${encodeURIComponent(form.id)}/viewer-columns`, {
            method: 'PUT',
            body: JSON.stringify({ hiddenColumns: list })
          });
        } catch (err) {
          // Roll back the checkbox if the save failed
          e.target.checked = !e.target.checked;
          if (e.target.checked) hidden.delete(key); else hidden.add(key);
          form.viewerHiddenColumns = [...hidden];
          this._showToast('Could not save: ' + err.message, 'danger');
        }
      });
    });
  }

  static async _loadOrCreateEventLink() {
    try {
      const data = await window.OrgContext.orgFetch('/viewer-links');
      // The most recent active link for this event — same dashboard regardless
      // of when it was created, so picking the newest is fine.
      const existing = (data.links || [])
        .filter(l => l.active !== false && l.eventFilter === this._currentViewerEventId)
        .sort((a, b) => (b.createdAt?._seconds || 0) - (a.createdAt?._seconds || 0))[0];

      if (existing) {
        this._currentViewerLink = existing;
        this._renderEventViewerLink();
        return;
      }

      // No link yet — mint one silently
      const created = await window.OrgContext.orgFetch('/viewer-links', {
        method: 'POST',
        body: JSON.stringify({ label: 'Dashboard Link', eventFilter: this._currentViewerEventId })
      });
      this._currentViewerLink = created.link;
      this._renderEventViewerLink();
    } catch (err) {
      document.getElementById('viewerLinkBody').innerHTML =
        `<p class="text-danger small mb-0">Could not load viewer link: ${this._escapeHtml(err.message)}</p>`;
    }
  }

  static _renderEventViewerLink() {
    const link = this._currentViewerLink;
    const body = document.getElementById('viewerLinkBody');
    const revokeBtn = document.getElementById('viewerLinkRevokeBtn');
    if (!link) {
      body.innerHTML = '<p class="text-muted small mb-0">No link yet.</p>';
      revokeBtn.style.display = 'none';
      return;
    }
    const url = `${window.location.origin}/view/${link.token}`;
    body.innerHTML = `
      <div class="viewer-link-display">
        <input type="text" class="form-control" id="viewerLinkUrlField" value="${this._escapeHtml(url)}" readonly>
        <button type="button" class="btn btn-org-primary" onclick="OrgDashboard.copyCurrentViewerLink()">
          <i class="bi bi-clipboard"></i> Copy
        </button>
      </div>`;
    revokeBtn.style.display = '';
    // Auto-select the URL so a Cmd/Ctrl+C also works
    setTimeout(() => document.getElementById('viewerLinkUrlField')?.select(), 50);
  }

  static async copyCurrentViewerLink() {
    const link = this._currentViewerLink;
    if (!link) return;
    const url = `${window.location.origin}/view/${link.token}`;
    try {
      await navigator.clipboard.writeText(url);
      this._showToast('Link copied to clipboard');
    } catch {
      // clipboard.writeText can fail in insecure contexts — fall back to the
      // input's native select-and-execCommand
      const field = document.getElementById('viewerLinkUrlField');
      field?.select();
      try { document.execCommand('copy'); this._showToast('Link copied'); }
      catch { this._showToast('Could not copy automatically — select and copy manually', 'warning'); }
    }
  }

  static async copyFormLink(formId) {
    // The custom link when the event has a live one (the server's
    // canonicalFormUrl rule, as the event page's Copy guest link uses), else
    // the link by id (task #1094).
    const f = ((this.dashboardData && this.dashboardData.forms) || []).find((x) => x.id === formId) || {};
    const slug = typeof f.slug === 'string' ? f.slug.trim() : '';
    const url = (slug && f.linkStatus === 'active' && /^[a-z0-9-]{3,50}$/i.test(slug))
      ? `${window.location.origin}/${slug}`
      : `${window.location.origin}/${formId}`;
    try {
      await navigator.clipboard.writeText(url);
      this._showToast('Form link copied to clipboard');
    } catch {
      this._showToast('Failed to copy link', 'warning');
    }
  }

  static async revokeViewerLink() {
    const link = this._currentViewerLink;
    if (!link) return;
    if (!confirm('Revoke this viewer link? Anyone with this URL will no longer have access. A new link will be generated next time you open this dialog.')) return;

    try {
      await window.OrgContext.orgFetch(`/viewer-links/${link.id}`, { method: 'DELETE' });
      this._currentViewerLink = null;
      this._showToast('Viewer link revoked');
      // Close the modal — next click on the eye icon mints a fresh link
      bootstrap.Modal.getInstance(document.getElementById('viewerLinkModal'))?.hide();
    } catch (err) {
      this._showToast('Error: ' + err.message, 'danger');
    }
  }

  // ============================================================================
  // EVENTS PAGE LINK
  // ============================================================================

  static _showEventsPageLink() {
    const section = document.getElementById('eventsPageLinkSection');
    if (!section || !this.currentOrg?.slug) return;

    const url = `${window.location.origin}/events/${this.currentOrg.slug}`;
    section.href = url;
    section.style.display = '';

    const urlEl = document.getElementById('eventsPageUrl');
    if (urlEl) urlEl.textContent = url;
  }

  static async copyEventsPageLink() {
    if (!this.currentOrg?.slug) return;
    const url = `${window.location.origin}/events/${this.currentOrg.slug}`;
    try {
      await navigator.clipboard.writeText(url);
      this._showToast('Events page link copied to clipboard');
    } catch {
      this._showToast('Failed to copy link', 'warning');
    }
  }

  // ============================================================================
  // ORG CRUD
  // ============================================================================

  // The one setup step before the dashboard (task #953): a name and a logo
  // UPLOAD (the old box wanted a logo URL, which nobody has to hand), with
  // the public events page header drawn as they type. The rest of the
  // profile waits on the "Finish your profile" card, not in front of it.
  static _newOrgLogoFile = null;
  static _newOrgLogoUrl = null;

  static showCreateOrgModal() {
    const modalEl = document.getElementById('createOrgModal');
    const nameEl = document.getElementById('newOrgName');
    const fileEl = document.getElementById('newOrgLogoFile');
    nameEl.value = '';
    fileEl.value = '';
    this._setNewOrgLogo(null);

    if (!modalEl.dataset.bound) {
      modalEl.dataset.bound = '1';
      nameEl.addEventListener('input', () => this._renderNewOrgPreview());
      nameEl.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); this.createOrg(); } });
      document.getElementById('newOrgLogoBtn').addEventListener('click', () => fileEl.click());
      document.getElementById('newOrgLogoRemove').addEventListener('click', () => { fileEl.value = ''; this._setNewOrgLogo(null); });
      fileEl.addEventListener('change', () => {
        const file = fileEl.files[0];
        if (!file) return;
        const problem = this._logoFileProblem(file);
        if (problem) { fileEl.value = ''; this._showToast(problem, 'danger'); return; }
        this._setNewOrgLogo(file);
      });
      document.getElementById('newOrgContinue').addEventListener('click', () => this.createOrg());
      document.getElementById('newOrgSkip').addEventListener('click', () => this.createOrg({ skip: true }));
      modalEl.addEventListener('shown.bs.modal', () => nameEl.focus());
    }
    bootstrap.Modal.getOrCreateInstance(modalEl).show();
  }

  static _setNewOrgLogo(file) {
    this._newOrgLogoFile = file;
    if (this._newOrgLogoUrl) URL.revokeObjectURL(this._newOrgLogoUrl);
    this._newOrgLogoUrl = file ? URL.createObjectURL(file) : null;
    const btnLabel = document.querySelector('#newOrgLogoBtn span');
    if (btnLabel) btnLabel.textContent = file ? 'Change logo' : 'Upload logo';
    const remove = document.getElementById('newOrgLogoRemove');
    if (remove) remove.hidden = !file;
    this._renderNewOrgPreview();
  }

  static _renderNewOrgPreview() {
    const name = document.getElementById('newOrgName').value.trim();
    document.getElementById('newOrgPreviewName').textContent = name || 'Your organization';
    const logoEl = document.getElementById('newOrgPreviewLogo');
    logoEl.textContent = '';
    if (this._newOrgLogoUrl) {
      const img = document.createElement('img');
      img.src = this._newOrgLogoUrl;
      img.alt = '';
      logoEl.appendChild(img);
    } else {
      logoEl.textContent = (name || 'A').charAt(0).toUpperCase();
    }
  }

  // A name for "Skip": the one they typed, else their company's from a work
  // email, else their own. Settings renames it any time.
  static _defaultOrgName() {
    const user = window.firebaseAuth?.currentUser;
    const domain = String(user?.email || '').split('@')[1] || '';
    const FREE_MAIL = /^(gmail|googlemail|yahoo|ymail|hotmail|outlook|live|msn|icloud|me|mac|aol|proton|protonmail|gmx|mail|walla|zoho|yandex)\./i;
    if (domain && !FREE_MAIL.test(domain)) {
      const word = domain.split('.')[0];
      if (word) return word.charAt(0).toUpperCase() + word.slice(1);
    }
    const first = String(user?.displayName || '').trim().split(/\s+/)[0];
    return first ? `${first}'s organization` : 'My organization';
  }

  static async createOrg({ skip = false } = {}) {
    let name = document.getElementById('newOrgName').value.trim();
    if (!name && skip) name = this._defaultOrgName();
    if (!name) {
      this._showToast('Please enter an organization name', 'warning');
      document.getElementById('newOrgName').focus();
      return;
    }
    const logoFile = skip ? null : this._newOrgLogoFile;
    const buttons = ['newOrgContinue', 'newOrgSkip'].map(id => document.getElementById(id));
    buttons.forEach(b => { b.disabled = true; });

    let org;
    try {
      org = await window.OrgContext.createOrganization(name, null);
    } catch (err) {
      buttons.forEach(b => { b.disabled = false; });
      this._showToast('Error: ' + err.message, 'danger');
      return;
    }
    // The logo goes up once the organization exists (its storage folder is
    // the org's id). A failed upload never undoes the organization.
    if (logoFile) {
      try {
        const url = await this._uploadLogoFile(logoFile, org.id);
        await window.OrgContext.orgFetch('', { method: 'PUT', body: JSON.stringify({ logo: url }) });
        org.logo = url;
      } catch (err) {
        this._showToast('Your organization is ready, but the logo did not upload. Add it in Settings.', 'warning');
      }
    }
    buttons.forEach(b => { b.disabled = false; });
    this._setNewOrgLogo(null);
    bootstrap.Modal.getInstance(document.getElementById('createOrgModal'))?.hide();

    // Re-init in-place instead of full page reload
    this._resetDashboardUI();
    document.getElementById('orgLoading').style.display = 'block';
    await this.init();
    this._showNextStep();
  }

  // Straight after setup: create an event, bring in a form, or subscribe.
  static _showNextStep() {
    const modalEl = document.getElementById('orgNextStepModal');
    if (!modalEl || !this.currentOrg) return;
    const hide = () => bootstrap.Modal.getInstance(modalEl)?.hide();
    document.getElementById('orgNextCreate').href = `/org-builder?org=${encodeURIComponent(this.currentOrg.id)}`;
    const P = OrgDashboard.CORPORATE_PRICING;
    document.getElementById('orgNextSubMonth').textContent = `Monthly $${P.monthly.amount}`;
    document.getElementById('orgNextSubYear').textContent = `Annual $${P.annual.amount.toLocaleString()}`;
    document.getElementById('orgNextSubHint').textContent = `Annual saves $${((P.monthly.amount * 12) - P.annual.amount).toLocaleString()}`;
    if (!modalEl.dataset.bound) {
      modalEl.dataset.bound = '1';
      document.getElementById('orgNextCreate').addEventListener('click', hide);
      document.getElementById('orgNextAssign').addEventListener('click', () => {
        modalEl.addEventListener('hidden.bs.modal', () => this.showAssignFormModal(), { once: true });
        hide();
      });
      document.getElementById('orgNextSubMonth').addEventListener('click', () => this.startSubscriptionCheckout('month'));
      document.getElementById('orgNextSubYear').addEventListener('click', () => this.startSubscriptionCheckout('year'));
    }
    bootstrap.Modal.getOrCreateInstance(modalEl).show();
  }

  // ============================================================================
  // FINISH YOUR PROFILE (task #953)
  // ============================================================================

  // The profile, step by step, each opening its part of Settings. Task #972:
  // the organization's name is step 1 - it is set at sign-up, so the card
  // starts a sixth of the way there rather than at zero.
  static _profileChecklistItems(org) {
    const st = org?.settings || {};
    const footer = st.footer || {};
    return [
      { label: 'Organization name', done: !!String(org?.name || '').trim(), field: 'settingsOrgName' },
      { label: 'Logo', done: !!org?.logo, field: 'settingsOrgLogo' },
      { label: 'Brand color', done: !!st.branding?.primaryColor, field: 'settingsBrandColorHex' },
      { label: 'Short description', done: !!footer.description, field: 'settingsFooterDescription' },
      { label: 'Contact details',
        done: !!(footer.website || footer.instagram || footer.contactEmail || footer.phone), field: 'settingsFooterWebsite' },
      { label: 'Event page',
        done: !!(st.eventsPageTitle && st.eventsPageDescription), field: 'settingsEventsPageTitle' }
    ];
  }

  /** White or ink on the brand color, whichever reads (WCAG luminance). */
  static _inkOn(hex) {
    const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ''));
    if (!m) return '#fff';
    const c = [0, 2, 4].map(i => parseInt(m[1].slice(i, i + 2), 16) / 255)
      .map(v => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
    const lum = 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
    return (1.05 / (lum + 0.05)) >= 4.5 ? '#fff' : '#16243f';
  }

  static _renderProfileChecklist() {
    const box = document.getElementById('orgProfileChecklist');
    if (!box) return;
    const org = this.currentOrg;
    const items = this._profileChecklistItems(org);
    const left = items.filter(i => !i.done);
    // Never on the public sample organization (?demo=1): it is a showroom.
    if (window.isDemoMode || this.userRole !== 'admin' || !org || org.settings?.profileChecklistDismissed || !left.length) {
      box.hidden = true;
      box.innerHTML = '';
      return;
    }
    const doneCount = items.length - left.length;
    const pct = Math.round((doneCount / items.length) * 100);
    // Task #990: done steps lead the track, so it fills from the left with no
    // gap however they were done; each keeps its order within its group, and
    // the numbers are positions.
    // Task #972: the steps as one connected chevron track, done ones filled
    // in the organization's own brand color with a tick. One SVG per step,
    // stretched to its width with a stroke that does not stretch, so the
    // points stay crisp and their corners round at any size.
    const brand = /^#[0-9a-f]{6}$/i.test(org.settings?.branding?.primaryColor || '') ? org.settings.branding.primaryColor : '#16243f';
    const ink = this._inkOn(brand);
    const chevron = (i) => {
      const d = i === 0 ? 'M3 3 H86 L97 20 L86 37 H3 Z' : 'M3 3 H86 L97 20 L86 37 H3 L14 20 Z';
      return `<svg class="org-pc-shape" viewBox="0 0 100 40" preserveAspectRatio="none" aria-hidden="true">`
        + `<path d="${d}" vector-effect="non-scaling-stroke" stroke-linejoin="round"/></svg>`;
    };
    box.style.setProperty('--org-pc-brand', brand);
    box.style.setProperty('--org-pc-ink', ink);
    box.innerHTML = `
      <div class="org-profile-checklist-head">
        <div class="org-pc-heading">
          <span class="org-pc-ring" style="--pct:${pct}" aria-hidden="true"></span>
          <div>
            <h3 class="org-profile-checklist-title">Finish your profile <span class="org-pc-pct">${pct}% done</span></h3>
            <p class="org-profile-checklist-sub">Guests see these on your events page and RSVP forms.</p>
          </div>
        </div>
        <button type="button" class="btn-close" data-dismiss-checklist aria-label="Hide this card"></button>
      </div>
      <ol class="org-pc-track" aria-label="${pct}% done">
        ${items.filter(i => i.done).concat(left).map((i, n) => `<li class="org-pc-step${i.done ? ' is-done' : ''}">
          <button type="button" data-field="${i.field}" aria-label="${this._escapeHtml(i.label)}${i.done ? ' (done)' : ''}">
            ${chevron(n)}
            <span class="org-pc-mark" aria-hidden="true">${i.done ? '<i class="bi bi-check-lg"></i>' : n + 1}</span>
            <span class="org-pc-label">${this._escapeHtml(i.label)}</span>
          </button>
        </li>`).join('')}
      </ol>`;
    box.hidden = false;
    box.querySelectorAll('[data-field]').forEach(btn => {
      btn.onclick = () => this._openSettingsField(btn.dataset.field);
    });
    box.querySelector('[data-dismiss-checklist]').onclick = () => this._dismissProfileChecklist();
  }

  static _openSettingsField(fieldId) {
    document.querySelector('#orgTabs [data-tab="settings"]')?.click();
    const el = document.getElementById(fieldId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => el.focus({ preventScroll: true }), 300);
  }

  static async _dismissProfileChecklist() {
    const org = this.currentOrg;
    if (!org) return;
    org.settings = { ...(org.settings || {}), profileChecklistDismissed: true };
    this._renderProfileChecklist();
    try {
      await window.OrgContext.orgFetch('', {
        method: 'PUT',
        body: JSON.stringify({ settings: { profileChecklistDismissed: true } })
      });
    } catch (err) {
      console.warn('Could not save the hidden profile card', err);
    }
  }

  static async switchOrg(orgId) {
    window.OrgContext.setCurrentOrg(orgId);
    // Re-init in-place instead of full page reload
    // No polling to stop
    this._resetDashboardUI();
    document.getElementById('orgLoading').style.display = 'block';
    await this.init();
  }

  // ============================================================================
  // FORM MANAGEMENT
  // ============================================================================

  static async showAssignFormModal() {
    new bootstrap.Modal(document.getElementById('assignFormModal')).show();

    // Load user's personal forms
    const container = document.getElementById('personalFormsList');
    try {
      const user = window.firebaseAuth?.currentUser;
      if (!user) return;

      const db = window.firebaseDb;
      const snap = await db.collection('rsvp_forms')
        .where('owner', '==', user.uid)
        .where('organizationId', '==', null)
        .get();

      if (snap.empty) {
        container.innerHTML = '<p class="text-muted">No personal forms available to assign.</p>';
        return;
      }

      container.innerHTML = snap.docs.map(doc => {
        const f = doc.data();
        return `
          <div class="d-flex align-items-center justify-content-between py-2 border-bottom">
            <div>
              <div class="fw-semibold" style="font-size: 0.9rem;">${this._escapeHtml(f.title || f.formTitle || 'Untitled')}</div>
              <div class="text-muted" style="font-size: 0.75rem;">${doc.id}</div>
            </div>
            <button class="btn btn-org-outline btn-sm" onclick="OrgDashboard.assignForm('${doc.id}')">
              Assign
            </button>
          </div>`;
      }).join('');

    } catch (err) {
      container.innerHTML = `<p class="text-danger small">${this._escapeHtml(err.message)}</p>`;
    }
  }

  static async assignForm(formId) {
    try {
      await window.OrgContext.orgFetch(`/forms/${formId}/assign`, { method: 'POST' });
      bootstrap.Modal.getInstance(document.getElementById('assignFormModal'))?.hide();
      this._showToast('Form assigned to organization');
      this.loadDashboard();
    } catch (err) {
      this._showToast('Error: ' + err.message, 'danger');
    }
  }

  static async removeForm(formId) {
    if (!confirm('Remove this form from the organization? The form will still exist in the owner\'s personal account.')) return;

    try {
      await window.OrgContext.orgFetch(`/forms/${formId}/unassign`, { method: 'DELETE' });
      this._showToast('Form removed from organization');
      this.loadDashboard();
    } catch (err) {
      this._showToast('Error: ' + err.message, 'danger');
    }
  }

  static async cloneForm(formId) {
    try {
      await window.OrgContext.orgFetch(`/forms/${formId}/clone`, { method: 'POST' });
      this._showToast('Form cloned successfully');
      this.loadDashboard();
    } catch (err) {
      this._showToast('Error: ' + err.message, 'danger');
    }
  }

  // ============================================================================
  // RESPONSE NOTIFICATIONS
  // ============================================================================

  static async openNotificationsModal(formId) {
    // Load current values from Firestore.
    let enabled = false;
    let recipient = '';
    let ownerEmail = '';
    try {
      const doc = await window.firebaseDb.collection('rsvp_forms').doc(formId).get();
      if (doc.exists) {
        const formData = doc.data();
        enabled = !!(formData.emailNotifications && formData.emailNotifications.enabled);
        recipient = (formData.emailNotifications && formData.emailNotifications.recipientEmail) || '';
        ownerEmail = formData.ownerEmail || '';
      }
    } catch (err) {
      console.warn('[org-notifications] load failed:', err);
    }

    const stale = document.getElementById('orgNotificationsModal');
    if (stale) stale.remove();

    const esc = (s) => this._escapeHtml(s);
    const escAttr = (s) => this._escapeHtml(s).replace(/"/g, '&quot;');

    const modalHtml = `
      <div class="modal fade" id="orgNotificationsModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">
                <i class="bi bi-envelope-at me-2"></i>Response Notifications
              </h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <p class="text-muted small mb-3">Get an email when someone responds, updates their RSVP, or declines.</p>
              <div class="form-check form-switch mb-3">
                <input class="form-check-input" type="checkbox" id="orgNotifEnabled" ${enabled ? 'checked' : ''}>
                <label class="form-check-label fw-semibold" for="orgNotifEnabled">
                  Email me on responses
                </label>
              </div>
              <div class="mb-0">
                <label for="orgNotifRecipient" class="form-label small mb-1">Send notifications to</label>
                <input type="email" class="form-control form-control-sm" id="orgNotifRecipient"
                  value="${escAttr(recipient || ownerEmail || '')}"
                  placeholder="${escAttr(ownerEmail || 'you@example.com')}"
                  autocomplete="email">
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Cancel</button>
              <button type="button" class="btn btn-primary" id="orgNotifSave">Save</button>
            </div>
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHtml);
    const modalEl = document.getElementById('orgNotificationsModal');
    const bsModal = new bootstrap.Modal(modalEl);

    modalEl.querySelector('#orgNotifSave').addEventListener('click', async () => {
      const enabledVal = modalEl.querySelector('#orgNotifEnabled').checked;
      const recipientVal = (modalEl.querySelector('#orgNotifRecipient').value || '').trim();
      const saveBtn = modalEl.querySelector('#orgNotifSave');
      saveBtn.disabled = true;
      try {
        // Dotted paths: a map value would REPLACE emailNotifications and wipe
        // the digest frequency and timezone saved beside these two fields.
        await window.firebaseDb.collection('rsvp_forms').doc(formId).update({
          'emailNotifications.enabled': enabledVal,
          'emailNotifications.recipientEmail': recipientVal || null
        });
        this._showToast('Notifications updated');
        bsModal.hide();
        this.loadDashboard();
      } catch (err) {
        console.error('[org-notifications] save failed:', err);
        this._showToast('Could not save notifications — please try again.', 'danger');
        saveBtn.disabled = false;
      }
    });

    modalEl.addEventListener('hidden.bs.modal', () => { modalEl.remove(); });
    bsModal.show();
  }

  // ============================================================================
  // SETTINGS
  // ============================================================================

  static _populateSettings() {
    const org = this.currentOrg;
    if (!org) return;

    document.getElementById('settingsOrgName').value = org.name || '';
    document.getElementById('settingsOrgLogo').value = org.logo || '';
    document.getElementById('settingsRequireApproval').checked = org.settings?.requireApproval || false;
    document.getElementById('settingsMaxCapacity').value = org.settings?.defaultMaxCapacity || '';

    // Logo preview
    const logoPreview = document.getElementById('settingsLogoPreview');
    if (logoPreview && org.logo) {
      logoPreview.innerHTML = `<img src="${this._escapeHtml(org.logo)}" alt="Logo">`;
    }

    // Settings logo file upload. Assigned, not added: this runs on every visit
    // to the tab, and stacked listeners uploaded the same logo once per visit.
    const settingsLogoInput = document.getElementById('settingsLogoFileInput');
    if (settingsLogoInput) {
      settingsLogoInput.onchange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        await this._handleLogoUpload(file);
        // Update preview in settings
        if (this.currentOrg.logo && logoPreview) {
          logoPreview.innerHTML = `<img src="${this._escapeHtml(this.currentOrg.logo)}" alt="Logo">`;
          document.getElementById('settingsOrgLogo').value = this.currentOrg.logo;
        }
      };
    }

    // Events page settings
    document.getElementById('settingsEventsPageEnabled').checked = org.settings?.eventsPageEnabled !== false;
    document.getElementById('settingsEventsPageTitle').value = org.settings?.eventsPageTitle || '';
    document.getElementById('settingsEventsPageDescription').value = org.settings?.eventsPageDescription || '';

    // Footer on guest pages (task #838)
    const footer = org.settings?.footer || {};
    const setVal = (id, v) => { const el = document.getElementById(id); if (el) el.value = v || ''; };
    const showEl = document.getElementById('settingsFooterShow');
    if (showEl) showEl.checked = footer.show !== false;
    setVal('settingsFooterDescription', footer.description);
    setVal('settingsFooterWebsite', footer.website);
    setVal('settingsFooterInstagram', footer.instagram ? '@' + footer.instagram : '');
    setVal('settingsFooterEmail', footer.contactEmail);
    setVal('settingsFooterPhone', footer.phone);

    // Brand color
    const brandColor = org.settings?.branding?.primaryColor || '#16243f';
    const brandColorEl = document.getElementById('settingsBrandColor');
    const brandHexEl = document.getElementById('settingsBrandColorHex');
    if (brandColorEl) {
      brandColorEl.value = brandColor;
      brandColorEl.oninput = () => {
        if (brandHexEl) brandHexEl.value = brandColorEl.value;
      };
    }
    if (brandHexEl) {
      brandHexEl.value = brandColor;
      brandHexEl.oninput = () => {
        const v = brandHexEl.value.trim();
        if (/^#[0-9a-fA-F]{6}$/.test(v) && brandColorEl) brandColorEl.value = v;
      };
    }

    // Subscription panel — fetch live state and render
    this._loadSubscription();

    // Gmail connection panel
    this.loadGmailStatus();

    // Integrations / API keys panel
    this.loadApiKeys();

    // Reply-to address
    const replyToEl = document.getElementById('settingsReplyTo');
    if (replyToEl) replyToEl.value = org.settings?.campaignReplyTo || '';

    // Sender name override — placeholder shows the current org name as default
    const senderNameEl = document.getElementById('settingsSenderName');
    if (senderNameEl) {
      senderNameEl.value = org.settings?.campaignSenderName || '';
      senderNameEl.placeholder = org.name || 'Your organization name';
    }

    // Per-org sender address — derived from org NAME (mirrors server side).
    // Renames flow through immediately; ugly collision-suffixed slugs stay hidden.
    const addrEl = document.getElementById('settingsOrgSenderAddress');
    if (addrEl) addrEl.textContent = `${this._senderLocalPart(org)}@events.quikrsvp.com`;

    // Dynamic "Sends as X" label on the fallback sender card
    const asLabelEl = document.getElementById('settingsOrgSenderAs');
    if (asLabelEl) asLabelEl.textContent = org.settings?.campaignSenderName || org.name || 'your org';

    // Enter in a field saves now rather than reloading the page.
    document.getElementById('orgSettingsForm').onsubmit = (e) => {
      e.preventDefault();
      this._flushSettings();
    };
    this._wireSettingsAutosave();
  }

  /**
   * Organization settings save themselves (task #1050). The Save button sat
   * below a long form, and hosts changed the name or brand colour and left
   * without it. The sender name and reply-to in the Email panel had no button
   * at all and were only ever saved by that one, further up the page.
   *
   * Typing saves after a pause, toggles and the colour picker at once, and
   * leaving a field saves what is in it. Email/URL/phone fields wait until
   * they parse, since the server refuses a half-typed address. Leaving the
   * page while a save is pending or in flight gets the browser's own "leave
   * site?" prompt; switching tabs inside the dashboard just saves.
   */
  static _wireSettingsAutosave() {
    if (this._settingsAutosaveWired) return;
    this._settingsAutosaveWired = true;
    const form = document.getElementById('orgSettingsForm');
    const OUTSIDE_FORM = ['settingsSenderName', 'settingsReplyTo'];
    const handler = (e) => {
      const el = e.target;
      if (!el || !(form?.contains(el) || OUTSIDE_FORM.includes(el.id))) return;
      if (el.type === 'file') return; // the logo upload saves itself
      if (e.type === 'input') {
        if (el.type === 'checkbox') return; // its change event follows
        if (/^(email|url|tel)$/.test(el.type) && el.value.trim() && !el.checkValidity()) {
          this._settingsDirty = true; // saved once it parses, or on leaving the field
          return;
        }
        this._scheduleSettingsSave(el.type === 'color' ? 400 : 900);
      } else {
        this._scheduleSettingsSave(0);
      }
    };
    document.addEventListener('input', handler);
    document.addEventListener('change', handler);
    window.addEventListener('beforeunload', (e) => {
      if (!this._settingsDirty && !this._settingsSaving) return;
      this._flushSettings();
      e.preventDefault();
      e.returnValue = '';
    });
  }

  static _scheduleSettingsSave(delay) {
    this._settingsDirty = true;
    clearTimeout(this._settingsTimer);
    this._settingsTimer = setTimeout(() => this._flushSettings(), delay);
  }

  static async _flushSettings() {
    clearTimeout(this._settingsTimer);
    if (!this._settingsDirty) return;
    if (this._settingsSaving) { this._settingsSaveAgain = true; return; }
    this._settingsSaving = true;
    this._settingsDirty = false;
    this._setSettingsStatus('saving');
    const ok = await this.saveSettings();
    this._settingsSaving = false;
    if (!ok) this._settingsDirty = true;
    else if (!this._settingsSaveAgain) this._setSettingsStatus('saved');
    if (this._settingsSaveAgain) {
      this._settingsSaveAgain = false;
      this._flushSettings();
    }
  }

  static _setSettingsStatus(state, message) {
    const el = document.getElementById('orgSettingsSaveStatus');
    if (!el) return;
    el.className = `org-save-status is-${state}`;
    el.innerHTML = state === 'saving'
      ? '<span class="spinner-border spinner-border-sm" aria-hidden="true"></span>Saving…'
      : state === 'saved'
        ? '<i class="bi bi-check-circle" aria-hidden="true"></i>All changes saved'
        : `<i class="bi bi-exclamation-circle" aria-hidden="true"></i>${this._escapeHtml(message || 'Not saved')}`;
  }

  static async _loadSubscription() {
    // Optimistic render from currentOrg cache
    const cachedStatus = this.currentOrg?.subscriptionStatus || 'trialing';
    this._renderSubscription({ status: cachedStatus, plan: 'corporate' });
    // GET /subscription is admin-only; editors and viewers keep the cached
    // status instead of a 403 on every load.
    if (this.userRole !== 'admin') return;

    try {
      const res = await window.OrgContext.orgFetch('/subscription');
      if (res?.subscription) {
        this._renderSubscription(res.subscription);
        this._renderLifecycleBanner(res.subscription.lifecycle);
      }
    } catch (err) {
      console.warn('Could not load subscription details', err);
    }
  }

  /**
   * Render the cancellation/lifecycle banner at the top of the main area.
   * States: grace (first 30 days post-cancel) | archived (30-90 days) | expired (90+)
   * active/trialing/past_due → no banner.
   */
  static _renderLifecycleBanner(lifecycle) {
    const banner = document.getElementById('orgLifecycleBanner');
    if (!banner) return;
    if (!lifecycle || ['active', 'trialing', 'past_due'].includes(lifecycle.state)) {
      banner.style.display = 'none';
      banner.innerHTML = '';
      return;
    }

    const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';

    let tone, icon, title, body, ctaLabel;
    if (lifecycle.state === 'grace') {
      tone = 'warn';
      icon = 'bi-clock-history';
      title = `Your subscription has been canceled.`;
      body = `You have full access until <strong>${fmtDate(lifecycle.graceEndsAt)}</strong> (${lifecycle.daysUntilReadOnly} day${lifecycle.daysUntilReadOnly === 1 ? '' : 's'} left). After that, the dashboard becomes read-only until ${fmtDate(lifecycle.archiveEndsAt)}. Reactivate anytime to keep sending campaigns.`;
      ctaLabel = 'Reactivate subscription';
    } else if (lifecycle.state === 'archived') {
      tone = 'danger';
      icon = 'bi-archive';
      title = `Your organization is archived.`;
      body = `The dashboard is read-only — you can export data but not edit, send campaigns, or accept new RSVPs. Reactivate to restore full access, or the organization will expire on <strong>${fmtDate(lifecycle.archiveEndsAt)}</strong>.`;
      ctaLabel = 'Reactivate subscription';
    } else if (lifecycle.state === 'expired') {
      tone = 'danger';
      icon = 'bi-exclamation-octagon';
      title = `This organization has expired.`;
      body = `Your subscription was canceled over 90 days ago. Data is scheduled for deletion. Contact support if you need to recover anything.`;
      ctaLabel = 'Reactivate subscription';
    }

    banner.className = `lifecycle-banner lifecycle-banner-${tone}`;
    banner.innerHTML = `
      <div class="lifecycle-banner-icon"><i class="bi ${icon}"></i></div>
      <div class="lifecycle-banner-body">
        <div class="lifecycle-banner-title">${title}</div>
        <div class="lifecycle-banner-text">${body}</div>
      </div>
      <button class="btn btn-org-primary btn-sm" onclick="OrgDashboard.openSubscriptionPortal()">
        <i class="bi bi-credit-card"></i><span>${ctaLabel}</span>
      </button>`;
    banner.style.display = '';
  }

  static _renderSubscription(sub) {
    const badge = document.getElementById('subStatusBadge');
    const meta = document.getElementById('subStatusMeta');
    const actions = document.getElementById('subActions');
    if (!badge || !meta || !actions) return;

    const status = sub.status || 'trialing';
    const labels = {
      trialing: 'Trialing',
      active: 'Active',
      past_due: 'Past due',
      canceled: 'Canceled',
      incomplete: 'Incomplete',
      incomplete_expired: 'Expired',
      unpaid: 'Past due'
    };
    badge.textContent = labels[status] || this._capitalize(status);
    badge.className = 'org-plan-badge sub-status sub-status-' + status;

    // Meta line: period end, cancel-at-period-end notice, or list pricing.
    // Reads the subscription's ACTUAL price when Stripe gave us one, so an
    // annual customer is never shown a monthly figure. Falls back to the list
    // monthly price (see CORPORATE_PRICING) for orgs with no Stripe sub yet.
    const priced = (typeof sub.unitAmount === 'number' && sub.interval)
      ? `$${(sub.unitAmount / 100).toLocaleString()} / ${sub.interval}`
      : `$${OrgDashboard.CORPORATE_PRICING.monthly.amount} / month`;
    let metaParts = [priced];
    if (sub.currentPeriodEnd) {
      const d = new Date(sub.currentPeriodEnd);
      const dateStr = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
      if (sub.cancelAtPeriodEnd) {
        metaParts = [`Cancels on ${dateStr}`];
      } else if (status === 'active' || status === 'trialing') {
        metaParts.push(`Next bill ${dateStr}`);
      }
    } else if (status === 'trialing') {
      metaParts.push('Trial — no payment method on file');
    }
    meta.textContent = metaParts.join(' · ');

    // Actions
    if (status === 'active' || status === 'past_due' || status === 'canceled' || sub.hasStripeCustomer) {
      actions.innerHTML = `
        <button class="btn btn-org-outline btn-sm" onclick="OrgDashboard.openSubscriptionPortal()">
          <i class="bi bi-credit-card"></i><span>Manage subscription</span>
        </button>`;
    } else {
      const P = OrgDashboard.CORPORATE_PRICING;
      const saving = (P.monthly.amount * 12) - P.annual.amount;
      actions.innerHTML = `
        <div class="sub-plan-choice">
          <button class="btn btn-org-primary btn-sm" onclick="OrgDashboard.startSubscriptionCheckout('month')">
            <i class="bi bi-rocket-takeoff"></i><span>Subscribe — $${P.monthly.amount}/mo</span>
          </button>
          <button class="btn btn-org-outline btn-sm" onclick="OrgDashboard.startSubscriptionCheckout('year')">
            <i class="bi bi-calendar-check"></i><span>Pay annually — $${P.annual.amount.toLocaleString()}</span>
          </button>
          <span class="sub-plan-hint">Annual saves $${saving.toLocaleString()} — two months free.</span>
        </div>`;
    }
  }

  // ---- Subscription gate (task #1018, Shahar) -------------------------------
  // A workspace that never subscribed (server: OrganizationService
  // .needsSubscription, sent as org.needsSubscription) collects replies but
  // waits for a subscription to show them, add guests, import, send
  // campaigns or add members. The server refuses those too (402
  // ORG_SUBSCRIPTION_REQUIRED); the dashboard says so before anyone clicks.

  static needsSubscription() {
    const org = window.OrgContext?.currentOrg || this.currentOrg;
    return !!(org && org.needsSubscription) && !window.__ORG_DEMO_ORG_ID__;
  }

  // ---- Counts before subscribing (task #1073, Shahar) -----------------------
  // The event page blurs its counters once replies exist (#1068); the summary
  // strip and the Events list showed the same numbers in the clear. They blur
  // the same way - only when there is a number to hide - and a click says why,
  // with Subscribe. The Events count stays: it is the host's own.

  static get COUNTS_LOCK_TEXT() { return 'Subscribe to unlock live response counts and guest analytics'; }

  static _countsLocked(hasReplies) { return !!hasReplies && this.needsSubscription(); }

  static _lockSummaryCounts(hasReplies) {
    const locked = this._countsLocked(hasReplies);
    ['statResponses', 'statAttending', 'statGuests'].forEach((id) => {
      const stat = document.getElementById(id)?.closest('.summary-stat');
      if (!stat) return;
      stat.classList.toggle('is-count-locked', locked);
      if (locked) {
        stat.setAttribute('role', 'button');
        stat.tabIndex = 0;
        stat.title = this.COUNTS_LOCK_TEXT;
      } else {
        stat.removeAttribute('role');
        stat.removeAttribute('tabindex');
        stat.removeAttribute('title');
      }
    });
  }

  /**
   * One listener for every blurred count. Capture phase, so a click on an
   * event card's counts says why instead of also opening the event.
   */
  static _bindCountsLock() {
    if (this._countsLockBound) return;
    this._countsLockBound = true;
    const onActivate = (e) => {
      const pop = document.getElementById('orgCountsLockPop');
      const locked = e.target.closest && e.target.closest('.is-count-locked');
      if (locked) {
        e.stopPropagation();
        e.preventDefault();
        this.showCountsLock(locked);
      } else if (pop && !pop.hidden && !pop.contains(e.target)) {
        pop.hidden = true;
      }
    };
    document.addEventListener('click', onActivate, true);
    document.addEventListener('keydown', (e) => {
      const pop = document.getElementById('orgCountsLockPop');
      if (e.key === 'Escape' && pop && !pop.hidden) { pop.hidden = true; return; }
      if ((e.key === 'Enter' || e.key === ' ') && e.target.classList?.contains('is-count-locked')) onActivate(e);
    }, true);
    // It follows its count while the page scrolls (a phone scrolls a little
    // on every tap) and closes once the count leaves the screen.
    const follow = () => this._placeCountsLock();
    window.addEventListener('scroll', follow, { passive: true, capture: true });
    window.addEventListener('resize', follow);
  }

  static _placeCountsLock() {
    const pop = document.getElementById('orgCountsLockPop');
    const anchor = this._countsLockAnchor;
    if (!pop || pop.hidden) return;
    const r = anchor && anchor.isConnected ? anchor.getBoundingClientRect() : null;
    if (!r || r.bottom < 0 || r.top > window.innerHeight) { pop.hidden = true; return; }
    const w = pop.offsetWidth;
    const left = Math.min(Math.max(r.left + r.width / 2 - w / 2, 16), window.innerWidth - w - 16);
    const below = r.bottom + 8 + pop.offsetHeight < window.innerHeight;
    pop.style.left = `${left}px`;
    pop.style.top = `${below ? r.bottom + 8 : r.top - 8 - pop.offsetHeight}px`;
  }

  /** "Subscribe to unlock..." with a Subscribe button, under the count clicked. */
  static showCountsLock(anchor) {
    let pop = document.getElementById('orgCountsLockPop');
    if (!pop) {
      pop = document.createElement('div');
      pop.id = 'orgCountsLockPop';
      pop.className = 'org-counts-lock-pop';
      pop.setAttribute('role', 'dialog');
      pop.innerHTML = `<p><i class="bi bi-lock-fill" aria-hidden="true"></i> ${this.COUNTS_LOCK_TEXT}</p>
        <button type="button" class="btn btn-org-primary btn-sm">Subscribe</button>`;
      pop.querySelector('button').onclick = () => { pop.hidden = true; this.openSubscribeModal(); };
      document.body.appendChild(pop);
    }
    this._countsLockAnchor = anchor;
    pop.hidden = false;
    this._placeCountsLock();
    pop.querySelector('button').focus({ preventScroll: true });
  }

  /** Dim the buttons that wait for a subscription (the event page dims its own). */
  static _applySubscriptionGate() {
    const gated = this.needsSubscription();
    const btn = document.getElementById('addMemberBtn');
    if (btn) {
      btn.disabled = gated;
      btn.title = gated ? 'Subscribe to add team members' : '';
    }
  }

  /**
   * "Subscribe here": Monthly or Annual, what the plan includes, and the
   * price - then Stripe Checkout (admins only: checkout is an admin route).
   */
  static openSubscribeModal(initial = 'month') {
    const P = OrgDashboard.CORPORATE_PRICING;
    const saving = (P.monthly.amount * 12) - P.annual.amount;
    const isAdmin = this.userRole === 'admin' || window.OrgContext?.currentOrg?.myRole === 'admin';
    document.getElementById('orgSubscribeModal')?.remove();
    const wrap = document.createElement('div');
    wrap.innerHTML = `
      <div class="modal fade" id="orgSubscribeModal" tabindex="-1" aria-labelledby="orgSubscribeTitle" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content org-subscribe-modal">
            <div class="modal-header">
              <h5 class="modal-title" id="orgSubscribeTitle">Subscribe to QuikRSVP Corporate</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <div class="org-sub-toggle" role="radiogroup" aria-label="Billing">
                <button type="button" role="radio" data-interval="month">Monthly</button>
                <button type="button" role="radio" data-interval="year">Annual <span class="org-sub-save">2 months free</span></button>
              </div>
              <div class="org-sub-price">
                <span class="org-sub-amount" data-price></span>
                <span class="org-sub-note" data-note></span>
              </div>
              <ul class="org-sub-features">
                <li><i class="bi bi-check2"></i>Unlimited events and responses</li>
                <li><i class="bi bi-check2"></i>Email campaigns, with 500 email credits a month</li>
                <li><i class="bi bi-check2"></i>Up to 15 team members with Admin, Editor and Viewer roles</li>
                <li><i class="bi bi-check2"></i>Seating, check-in, approvals and read-only viewer links</li>
                <li><i class="bi bi-check2"></i>Contacts, templates and your public events page</li>
                <li><i class="bi bi-check2"></i>Forms in 70+ languages, priority support</li>
              </ul>
              ${isAdmin ? '' : '<p class="org-sub-admin-only"><i class="bi bi-info-circle"></i> Only an admin of this organization can subscribe. Ask one of them to open this page.</p>'}
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-org-outline btn-sm" data-bs-dismiss="modal">Not now</button>
              <button type="button" class="btn btn-org-primary btn-sm" data-sub-go${isAdmin ? '' : ' disabled'}>
                <i class="bi bi-rocket-takeoff"></i><span data-go-label></span>
              </button>
            </div>
          </div>
        </div>
      </div>`;
    document.body.appendChild(wrap.firstElementChild);
    const modalEl = document.getElementById('orgSubscribeModal');
    let interval = initial === 'year' ? 'year' : 'month';
    const draw = () => {
      modalEl.querySelectorAll('[data-interval]').forEach((b) => {
        const on = b.dataset.interval === interval;
        b.classList.toggle('active', on);
        b.setAttribute('aria-checked', String(on));
      });
      modalEl.querySelector('[data-price]').textContent = interval === 'year'
        ? `$${P.annual.amount.toLocaleString()} / year` : `$${P.monthly.amount} / month`;
      modalEl.querySelector('[data-note]').textContent = interval === 'year'
        ? `Your first 30 days are free. Payment details are taken now and nothing is charged until the trial ends. Saves $${saving.toLocaleString()} a year.`
        : 'Billed monthly, cancel anytime. 30-day money-back guarantee.';
      modalEl.querySelector('[data-go-label]').textContent = interval === 'year' ? 'Start the annual plan' : 'Subscribe monthly';
    };
    modalEl.querySelectorAll('[data-interval]').forEach((b) => b.addEventListener('click', () => { interval = b.dataset.interval; draw(); }));
    modalEl.querySelector('[data-sub-go]').addEventListener('click', (e) => {
      e.currentTarget.disabled = true;
      this.startSubscriptionCheckout(interval).finally(() => { e.currentTarget.disabled = false; });
    });
    modalEl.addEventListener('hidden.bs.modal', () => modalEl.remove());
    draw();
    bootstrap.Modal.getOrCreateInstance(modalEl).show();
  }

  static async startSubscriptionCheckout(interval) {
    try {
      const res = await window.OrgContext.orgFetch('/subscription/checkout', {
        method: 'POST',
        body: JSON.stringify({
          origin: window.location.origin,
          interval: interval === 'year' ? 'year' : 'month'
        })
      });
      if (res?.url) {
        window.location.href = res.url;
      } else {
        this._showToast('Could not start checkout', 'danger');
      }
    } catch (err) {
      console.error('Checkout error', err);
      this._showToast(err.message || 'Could not start checkout', 'danger');
    }
  }

  // ============================================================================
  // GMAIL OAUTH
  // ============================================================================

  static async loadGmailStatus() {
    if (this.userRole !== 'admin') return; // GET /gmail/status is admin-only
    try {
      const res = await window.OrgContext.orgFetch('/gmail/status');
      this._renderGmailStatus(res);
    } catch (err) {
      console.warn('Gmail status load failed:', err);
    }
  }

  static _renderGmailStatus(s) {
    const label = document.getElementById('gmailStatusLabel');
    const actions = document.getElementById('gmailActions');
    const card = document.getElementById('gmailConnectionCard');
    const list = document.getElementById('gmailAccountList');
    if (!label || !actions) return;

    // Several mailboxes per org (task #628). A payload without the list is an
    // older response describing one account — treat it as a list of one rather
    // than showing nothing.
    const accounts = Array.isArray(s?.accounts)
      ? s.accounts
      : (s?.connected ? [{ id: 'gmail', email: s.email, name: s.name,
                           sentToday: s.sentToday, dailyQuota: s.dailyQuota, isDefault: true }] : []);
    const defaultSender = s?.defaultSender || (accounts.find(a => a.isDefault) || {}).id || 'quikrsvp';

    if (accounts.length) {
      if (card) card.classList.add('sender-card-connected');
      label.innerHTML = accounts.length === 1
        ? `<span class="sender-status-dot connected"></span>Connected as <strong>${this._escapeHtml(accounts[0].email)}</strong>`
        : `<span class="sender-status-dot connected"></span><strong>${accounts.length}</strong> accounts connected`;
      actions.innerHTML = `
        <button class="btn btn-org-primary btn-sm" onclick="OrgDashboard.connectGmail()">
          <i class="bi bi-plus-circle"></i><span>Connect another</span>
        </button>`;
    } else {
      if (card) card.classList.remove('sender-card-connected');
      label.textContent = 'Not connected';
      actions.innerHTML = `
        <button class="btn btn-org-primary btn-sm" onclick="OrgDashboard.connectGmail()">
          <i class="bi bi-plus-circle"></i><span>Connect Gmail</span>
        </button>`;
    }

    if (!list) return;
    // One row per mailbox: who it sends as, how much of its own daily quota it
    // has used, whether it is the default, and how to change either.
    list.innerHTML = accounts.map(a => `
      <div class="gmail-account-row${a.id === defaultSender ? ' is-default' : ''}">
        <div class="gmail-account-info">
          <div class="gmail-account-email">
            ${this._escapeHtml(a.email)}
            ${a.id === defaultSender ? '<span class="sender-card-badge">Default</span>' : ''}
          </div>
          <div class="gmail-account-meta">
            ${a.name ? this._escapeHtml(a.name) + ' · ' : ''}${a.sentToday || 0} / ${a.dailyQuota || 2000} sent today
          </div>
        </div>
        <div class="gmail-account-actions">
          ${a.id === defaultSender ? '' : `
          <button class="btn btn-org-ghost btn-xs" onclick="OrgDashboard.setDefaultSender('${this._escapeHtml(a.id)}')">
            <i class="bi bi-star"></i><span>Make default</span>
          </button>`}
          <button class="btn btn-org-ghost btn-xs" onclick="OrgDashboard.disconnectGmail('${this._escapeHtml(a.id)}', '${this._escapeHtml(a.email)}')">
            <i class="bi bi-x-circle"></i><span>Disconnect</span>
          </button>
        </div>
      </div>`).join('');

    // Invitations that have been sent and not taken up yet (task #628 round 3).
    // An admin who invited a colleague yesterday could not previously tell that
    // they had — the list looked exactly as it did before they typed anything.
    const pending = Array.isArray(s?.pendingInvites) ? s.pendingInvites : [];
    list.innerHTML += pending.map(p => `
      <div class="gmail-account-row is-pending">
        <div class="gmail-account-info">
          <div class="gmail-account-email">
            ${this._escapeHtml(p.email)}
            <span class="sender-card-badge ${p.expired ? 'is-expired' : 'is-pending'}">${p.expired ? 'Invitation expired' : 'Pending'}</span>
          </div>
          <div class="gmail-account-meta">${this._escapeHtml(this._inviteMeta(p))}</div>
        </div>
        <div class="gmail-account-actions">
          <button class="btn btn-org-ghost btn-xs" onclick="OrgDashboard.resendSenderInvite('${this._escapeHtml(p.email)}')">
            <i class="bi bi-envelope-arrow-up"></i><span>Resend link</span>
          </button>
          <button class="btn btn-org-ghost btn-xs" onclick="OrgDashboard.cancelSenderInvite('${this._escapeHtml(p.email)}')">
            <i class="bi bi-x-circle"></i><span>Cancel</span>
          </button>
        </div>
      </div>`).join('');

    // Choosing the fallback as the default is a real choice, not just "no
    // accounts" — an org can connect a mailbox and still want campaigns to go
    // out on the QuikRSVP address by default.
    if (accounts.length || pending.length) {
      list.innerHTML += `
        <div class="gmail-account-row${defaultSender === 'quikrsvp' ? ' is-default' : ''}">
          <div class="gmail-account-info">
            <div class="gmail-account-email">QuikRSVP sender
              ${defaultSender === 'quikrsvp' ? '<span class="sender-card-badge">Default</span>' : ''}</div>
            <div class="gmail-account-meta">Always available, no setup</div>
          </div>
          <div class="gmail-account-actions">
            ${defaultSender === 'quikrsvp' ? '' : `
            <button class="btn btn-org-ghost btn-xs" onclick="OrgDashboard.setDefaultSender('quikrsvp')">
              <i class="bi bi-star"></i><span>Make default</span>
            </button>`}
          </div>
        </div>`;
    }
  }

  /**
   * The one line under a pending address: when it was sent, whether it has
   * been sent more than once, and how long the link has left.
   *
   * "Invited 2 days ago" alone leaves the admin guessing whether the link is
   * still good — which is the decision the row exists to support.
   */
  static _inviteMeta(p) {
    const rel = (ms) => {
      if (!ms) return null;
      const mins = Math.round((Date.now() - ms) / 60000);
      if (mins < 1) return 'just now';
      if (mins < 60) return mins + (mins === 1 ? ' minute ago' : ' minutes ago');
      const hrs = Math.round(mins / 60);
      if (hrs < 24) return hrs + (hrs === 1 ? ' hour ago' : ' hours ago');
      const days = Math.round(hrs / 24);
      return days + (days === 1 ? ' day ago' : ' days ago');
    };
    const parts = [];
    const when = rel(p.lastSentAt);
    parts.push(when ? `Invitation sent ${when}` : 'Invitation sent');
    if (p.sendCount > 1) parts.push(`sent ${p.sendCount} times`);
    if (p.expired) {
      parts.push('the link has expired');
    } else if (p.expiresAt) {
      const hrs = Math.max(0, Math.round((p.expiresAt - Date.now()) / 3600000));
      parts.push(hrs >= 24
        ? `link valid for ${Math.round(hrs / 24)} more day${Math.round(hrs / 24) === 1 ? '' : 's'}`
        : `link valid for ${hrs} more hour${hrs === 1 ? '' : 's'}`);
    }
    parts.push('waiting for them to sign in to Google');
    return parts.join(' · ');
  }

  /** Send the invitation link again — a fresh link, same address. */
  static async resendSenderInvite(email) {
    try {
      await window.OrgContext.orgFetch('/gmail/invite', {
        method: 'POST', body: JSON.stringify({ email })
      });
      this._showToast(`Invitation re-sent to ${email}.`);
      this.loadGmailStatus();
    } catch (err) {
      this._showToast('Could not re-send that invitation: ' + err.message, 'danger');
    }
  }

  /**
   * Stop waiting on an invitation.
   *
   * This forgets the row. The link already in their inbox is a signed,
   * stateless token with its own expiry — there is nothing to revoke, and
   * the confirmation says so rather than implying the link is dead.
   */
  static async cancelSenderInvite(email) {
    if (!window.confirm(
      `Stop waiting on ${email}?\n\n`
      + 'This removes it from your list. If they still have the invitation email, '
      + 'their link keeps working until it expires on its own.')) return;
    try {
      await window.OrgContext.orgFetch('/gmail/invite', {
        method: 'DELETE', body: JSON.stringify({ email })
      });
      this._showToast(`Stopped waiting on ${email}.`);
      this.loadGmailStatus();
    } catch (err) {
      this._showToast('Could not cancel that invitation: ' + err.message, 'danger');
    }
  }

  static async setDefaultSender(sender) {
    try {
      await window.OrgContext.orgFetch('/gmail/default', {
        method: 'POST',
        body: JSON.stringify({ sender })
      });
      this._showToast('Default sender updated');
      this.loadGmailStatus();
    } catch (err) {
      this._showToast('Could not change the default sender: ' + err.message, 'danger');
    }
  }

  /**
   * Invite a colleague's mailbox into the sender list (task #628 round 2).
   *
   * Nothing is connected here — we mail them a link and they sign in to their
   * own Google. That's the whole point: an admin who wants their coworker as a
   * sender doesn't have, and must never have, their coworker's password.
   */
  static async inviteSenderAddress() {
    const input = document.getElementById('senderInviteEmail');
    if (!input) return;
    const email = (input.value || '').trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this._showToast('Enter a valid email address.', 'danger');
      input.focus();
      return;
    }
    try {
      const res = await window.OrgContext.orgFetch('/gmail/invite', {
        method: 'POST',
        body: JSON.stringify({ email })
      });
      input.value = '';
      if (res && res.alreadyConnected) {
        this._showToast(`${email} is already one of your sending addresses.`, 'info');
        return;
      }
      // Say what happens NEXT, not just "sent" — nothing appears in the list
      // until they have actually clicked through, and an admin watching an
      // unchanged list would otherwise think it had failed.
      this._showToast(`Invitation sent to ${email}. Their address appears here once they've signed in to Google.`);
      // Draw the pending row straight away — an unchanged list is what made
      // an admin unsure the invitation had gone at all (task #628 round 3).
      this.loadGmailStatus();
    } catch (err) {
      this._showToast('Could not send that invitation: ' + err.message, 'danger');
    }
  }

  static async connectGmail() {
    try {
      const res = await window.OrgContext.orgFetch('/gmail/auth-url');
      if (!res?.url) {
        this._showToast('Could not start Gmail connection', 'danger');
        return;
      }
      // Open in a popup window
      const popup = window.open(res.url, 'gmail-auth', 'width=520,height=680,menubar=no,toolbar=no');

      // Listen for the postMessage from the callback page
      const handler = (event) => {
        if (!event.data || typeof event.data !== 'object') return;
        if (event.data.type === 'gmail-auth-success') {
          window.removeEventListener('message', handler);
          this._showToast(`Gmail connected as ${event.data.email}`);
          this.loadGmailStatus();
        } else if (event.data.type === 'gmail-auth-error') {
          window.removeEventListener('message', handler);
          this._showToast('Gmail connection failed: ' + event.data.error, 'danger');
        }
      };
      window.addEventListener('message', handler);

      // Fallback poll in case postMessage doesn't fire (popup blocked or closed early)
      const poll = setInterval(() => {
        if (popup && popup.closed) {
          clearInterval(poll);
          setTimeout(() => this.loadGmailStatus(), 500);
        }
      }, 1000);
    } catch (err) {
      this._showToast('Could not start Gmail connection: ' + err.message, 'danger');
    }
  }

  static async disconnectGmail(accountId, email) {
    // Name the address being disconnected — with several connected, "Disconnect
    // Gmail?" does not say which one is about to stop sending (task #628).
    const what = email ? `Disconnect ${email}?` : 'Disconnect Gmail?';
    if (!confirm(`${what} Campaigns that used it will send via QuikRSVP\u2019s default sender instead.`)) return;
    try {
      await window.OrgContext.orgFetch('/gmail/disconnect', {
        method: 'POST',
        body: JSON.stringify(accountId ? { accountId } : {})
      });
      this._showToast('Gmail disconnected');
      this.loadGmailStatus();
    } catch (err) {
      this._showToast('Disconnect failed: ' + err.message, 'danger');
    }
  }

  // ============================================================================
  // INTEGRATIONS / API KEYS (Zapier, CRM sync)
  // ============================================================================

  static async loadApiKeys() {
    const list = document.getElementById('apiKeysList');
    if (!list) return;
    const orgId = window.OrgContext.getCurrentOrgId();
    if (!orgId) return;
    try {
      const res = await window.OrgContext.apiFetch(`/api/integrations/orgs/${orgId}/api-keys`);
      this._renderApiKeys(res.apiKeys || [], res.configured !== false);
    } catch (err) {
      list.innerHTML = `<div class="text-danger small">Could not load API keys: ${this._escapeHtml(err.message)}</div>`;
    }
  }

  static _renderApiKeys(keys, configured) {
    const list = document.getElementById('apiKeysList');
    if (!list) return;
    if (!configured) {
      list.innerHTML = '<div class="text-warning small">Integrations are not enabled on the server yet. Contact support.</div>';
      return;
    }
    const active = keys.filter(k => k.active !== false);
    if (!active.length) {
      list.innerHTML = '<div class="text-muted small">No API keys yet. Create one below to connect Zapier.</div>';
      return;
    }
    list.innerHTML = active.map(k => `
      <div class="sender-card">
        <div class="sender-card-info">
          <div class="sender-card-icon"><i class="bi bi-key"></i></div>
          <div>
            <div class="sender-card-title">${this._escapeHtml(k.label || 'API key')}</div>
            <div class="sender-card-status">Key <code>qr_${this._escapeHtml(k.prefix || '')}...</code>${k.lastUsedAt ? ' &middot; last used ' + this._formatApiKeyDate(k.lastUsedAt) : ' &middot; never used'}</div>
          </div>
        </div>
        <div class="sender-card-actions">
          <button class="btn btn-org-ghost btn-sm" onclick="OrgDashboard.revokeApiKey('${k.id}')">
            <i class="bi bi-x-circle"></i><span>Revoke</span>
          </button>
        </div>
      </div>`).join('');
  }

  static _formatApiKeyDate(ts) {
    try {
      const ms = ts && ts._seconds ? ts._seconds * 1000
        : (ts && ts.seconds ? ts.seconds * 1000 : Date.parse(ts));
      if (!ms || isNaN(ms)) return 'recently';
      return new Date(ms).toLocaleDateString();
    } catch { return 'recently'; }
  }

  static async createApiKey() {
    const orgId = window.OrgContext.getCurrentOrgId();
    if (!orgId) return;
    const labelEl = document.getElementById('apiKeyLabel');
    const label = (labelEl && labelEl.value || '').trim();
    try {
      const res = await window.OrgContext.apiFetch(`/api/integrations/orgs/${orgId}/api-keys`, {
        method: 'POST',
        body: JSON.stringify({ label })
      });
      const key = res.apiKey && res.apiKey.key;
      if (key) {
        const reveal = document.getElementById('newApiKeyReveal');
        const field = document.getElementById('newApiKeyField');
        if (field) field.value = key;
        if (reveal) reveal.style.display = '';
        setTimeout(() => field && field.select(), 50);
      }
      if (labelEl) labelEl.value = '';
      this._showToast('API key created - copy it now, it will not be shown again');
      this.loadApiKeys();
    } catch (err) {
      this._showToast('Could not create key: ' + err.message, 'danger');
    }
  }

  static async copyNewApiKey() {
    const field = document.getElementById('newApiKeyField');
    if (!field || !field.value) return;
    try {
      await navigator.clipboard.writeText(field.value);
      this._showToast('API key copied to clipboard');
    } catch {
      field.select();
      try { document.execCommand('copy'); this._showToast('API key copied'); }
      catch { this._showToast('Select and copy the key manually', 'warning'); }
    }
  }

  static async revokeApiKey(keyId) {
    if (!confirm('Revoke this API key? Any Zapier connection using it will stop working immediately.')) return;
    const orgId = window.OrgContext.getCurrentOrgId();
    if (!orgId) return;
    try {
      await window.OrgContext.apiFetch(`/api/integrations/orgs/${orgId}/api-keys/${keyId}`, { method: 'DELETE' });
      this._showToast('API key revoked');
      const reveal = document.getElementById('newApiKeyReveal');
      if (reveal) reveal.style.display = 'none';
      this.loadApiKeys();
    } catch (err) {
      this._showToast('Could not revoke key: ' + err.message, 'danger');
    }
  }

  static async openSubscriptionPortal() {
    try {
      const res = await window.OrgContext.orgFetch('/subscription/portal', {
        method: 'POST',
        body: JSON.stringify({ returnUrl: window.location.origin + '/org-dashboard' })
      });
      if (res?.url) {
        window.location.href = res.url;
      } else {
        this._showToast('Could not open subscription portal', 'danger');
      }
    } catch (err) {
      console.error('Portal error', err);
      this._showToast(err.message || 'Could not open subscription portal', 'danger');
    }
  }

  // Called by _flushSettings(); returns whether the save went through.
  static async saveSettings() {
    const name = document.getElementById('settingsOrgName').value.trim();
    // The server ignores a blank name, so saving one would only blank the
    // header here. Say so and wait for a name.
    if (!name) {
      this._setSettingsStatus('error', "Organization name can't be empty");
      return false;
    }
    const logo = document.getElementById('settingsOrgLogo').value.trim() || null;
    const requireApproval = document.getElementById('settingsRequireApproval').checked;
    const maxCap = document.getElementById('settingsMaxCapacity').value;
    const eventsPageEnabled = document.getElementById('settingsEventsPageEnabled').checked;
    const eventsPageTitle = document.getElementById('settingsEventsPageTitle').value.trim() || null;
    const eventsPageDescription = document.getElementById('settingsEventsPageDescription').value.trim() || null;

    // Brand color — validate hex, fall back to default if invalid
    let primaryColor = document.getElementById('settingsBrandColor')?.value || null;
    const hexInput = document.getElementById('settingsBrandColorHex')?.value?.trim();
    if (hexInput && /^#[0-9a-fA-F]{6}$/.test(hexInput)) primaryColor = hexInput;
    if (primaryColor === '#16243f') primaryColor = null; // null = default, don't store

    const campaignReplyTo = document.getElementById('settingsReplyTo')?.value?.trim() || null;
    const campaignSenderName = document.getElementById('settingsSenderName')?.value?.trim() || null;
    // Task #838: checked and cleaned on the server (the same rules as an
    // Event Planner's branding); a bad website or email comes back as the error.
    const fval = (id) => document.getElementById(id)?.value?.trim() || '';
    const footer = {
      show: document.getElementById('settingsFooterShow')?.checked !== false,
      description: fval('settingsFooterDescription'),
      website: fval('settingsFooterWebsite'),
      instagram: fval('settingsFooterInstagram'),
      contactEmail: fval('settingsFooterEmail'),
      phone: fval('settingsFooterPhone')
    };

    const settings = {
      requireApproval,
      defaultMaxCapacity: maxCap ? parseInt(maxCap) : null,
      eventsPageEnabled,
      eventsPageTitle,
      eventsPageDescription,
      campaignReplyTo,
      campaignSenderName,
      footer,
      branding: {
        primaryColor,
        logo
      }
    };

    try {
      await window.OrgContext.orgFetch('', {
        method: 'PUT',
        body: JSON.stringify({ name, logo, settings })
      });

      // Keep the local copy in step with ALL of what was saved: the tab
      // repopulates from it on every visit, and with saving automatic a stale
      // toggle shown there would be written straight back.
      this.currentOrg.name = name;
      this.currentOrg.logo = logo;
      this.currentOrg.settings = {
        ...(this.currentOrg.settings || {}),
        ...settings,
        branding: { ...(this.currentOrg.settings?.branding || {}), ...settings.branding }
      };
      this._renderHeader();
      this._renderProfileChecklist();
      return true;
    } catch (err) {
      this._setSettingsStatus('error', 'Not saved: ' + err.message);
      this._showToast('Settings not saved: ' + err.message, 'danger');
      return false;
    }
  }

  static resetBrandColor() {
    const el = document.getElementById('settingsBrandColor');
    const hex = document.getElementById('settingsBrandColorHex');
    if (el) el.value = '#16243f';
    if (hex) hex.value = '#16243f';
    this._scheduleSettingsSave(0);
  }

  // ============================================================================
  // LOGO UPLOAD
  // ============================================================================

  static triggerLogoUpload() {
    const input = document.getElementById('orgLogoFileInput');
    if (input) input.click();
  }

  // Why a logo file can't be used, or null. iOS sometimes delivers HEIC
  // files with an empty file.type — trust the extension in that case.
  static _logoFileProblem(file) {
    const ALLOWED = ['image/jpeg', 'image/png', 'image/heic', 'image/heif'];
    const ALLOWED_EXTS = ['.jpg', '.jpeg', '.png', '.heic', '.heif'];
    const fileType = (file.type || '').toLowerCase();
    const fileName = (file.name || '').toLowerCase();
    const hasValidExt = ALLOWED_EXTS.some(ext => fileName.endsWith(ext));
    const hasValidType = fileType === '' ? hasValidExt : ALLOWED.includes(fileType);
    if (!hasValidType || !hasValidExt) return 'Please upload a JPG, PNG, or HEIC image';
    if (file.size > 5 * 1024 * 1024) return 'Image must be under 5MB';
    return null;
  }

  // Compresses and stores a logo under the org's folder; returns its URL.
  static async _uploadLogoFile(file, orgId) {
    const compressed = await this._compressLogo(file);
    const storage = window.firebaseStorage || firebase.storage();
    const ref = storage.ref(`org-logos/${orgId}/logo_${Date.now()}.jpg`);
    const snapshot = await ref.put(compressed);
    return snapshot.ref.getDownloadURL();
  }

  static async _handleLogoUpload(file) {
    const problem = this._logoFileProblem(file);
    if (problem) {
      this._showToast(problem, 'danger');
      return;
    }

    try {
      const overlay = document.querySelector('.org-logo-upload-overlay');
      if (overlay) overlay.innerHTML = '<div class="spinner-border spinner-border-sm text-light"></div>';

      const downloadURL = await this._uploadLogoFile(file, this.currentOrg.id);

      // Save to org settings
      await window.OrgContext.orgFetch('', {
        method: 'PUT',
        body: JSON.stringify({ logo: downloadURL })
      });

      this.currentOrg.logo = downloadURL;
      this._renderHeader();
      this._renderProfileChecklist();

      // Update settings input if visible
      const logoInput = document.getElementById('settingsOrgLogo');
      if (logoInput) logoInput.value = downloadURL;

      this._showToast('Logo updated');
    } catch (err) {
      this._showToast('Failed to upload logo: ' + err.message, 'danger');
      this._renderHeader();
    }
  }

  static _compressLogo(file, maxSize = 256, quality = 0.85) {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        let { width, height } = img;
        if (width > maxSize || height > maxSize) {
          const scale = maxSize / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          URL.revokeObjectURL(objectUrl);
          if (!blob) { reject(new Error('Could not encode this image. Please try a different file.')); return; }
          resolve(new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
        }, 'image/jpeg', quality);
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('This image format could not be read by your browser. Please convert to JPG or PNG and try again.'));
      };
      img.src = objectUrl;
    });
  }

  // ============================================================================
  // UTILITIES
  // ============================================================================

  static _escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Encode an arbitrary string as a JS literal that's also safe to drop inside
   * a double-quoted HTML attribute (e.g. an inline onclick). JSON.stringify
   * handles the JS-string escaping; we then HTML-encode the wrapping double
   * quotes and any `&` so the attribute parser doesn't truncate the value.
   */
  static _jsAttr(str) {
    return JSON.stringify(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;');
  }

  static _capitalize(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  /**
   * Task #902: toasts used to pile up. `container.innerHTML += …` re-created
   * every toast already on screen, which cut them loose from their Bootstrap
   * timers, so they never hid - five conflict notices stacked down the right
   * edge over the chat button. Now a toast is appended without touching the
   * others, the same message (or the same opts.key) REPLACES the one showing,
   * at most three show at once, and warnings are dark text on yellow.
   */
  static _showToast(message, type = 'success', opts = {}) {
    const container = document.getElementById('toast-container');
    if (!container) return;
    const key = String(opts.key || message);
    [...container.querySelectorAll('.toast')].forEach((el) => { if (el.dataset.toastKey === key) el.remove(); });
    while (container.querySelectorAll('.toast').length >= 3) container.querySelector('.toast').remove();
    const colors = type === 'danger' ? 'bg-danger text-white' : type === 'warning' ? 'bg-warning text-dark' : 'bg-dark text-white';
    const closeClass = type === 'warning' ? 'btn-close' : 'btn-close btn-close-white';
    const toastEl = document.createElement('div');
    toastEl.className = `toast align-items-center ${colors} border-0`;
    toastEl.setAttribute('role', 'alert');
    toastEl.dataset.bsAutohide = 'true';
    toastEl.dataset.bsDelay = type === 'warning' || type === 'danger' ? '6000' : '4000';
    toastEl.dataset.toastKey = key;
    toastEl.innerHTML = `
        <div class="d-flex">
          <div class="toast-body">${this._escapeHtml(message)}</div>
          <button type="button" class="${closeClass} me-2 m-auto" data-bs-dismiss="toast" aria-label="Close"></button>
        </div>`;
    container.appendChild(toastEl);
    new bootstrap.Toast(toastEl).show();
    toastEl.addEventListener('hidden.bs.toast', () => toastEl.remove());
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => OrgDashboard.init());
